import { useState, useEffect, useMemo } from "react";
import { useAuth } from "@/App";
import usePageTitle from "@/hooks/usePageTitle";
import ClientHealthPopover from "@/components/ClientHealthPopover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  BookOpen,
  Plus,
  RefreshCw,
  Search,
  Edit3,
  Trash2,
  ChevronRight,
  ChevronLeft,
  Calendar,
  Users,
  DollarSign,
  Package,
  TrendingUp,
  Eye,
  Eraser,
  EyeOff,
  ArrowLeft,
  FileSpreadsheet,
  Zap,
  CreditCard,
  IndianRupee,
  Table,
  LayoutGrid,
  X,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Filter,
  SlidersHorizontal,
  XCircle,
  List,
  Truck,
  Check,
  Building2,
  UserCheck,
  Briefcase
} from "lucide-react";
import ClientPriceList from "@/components/ClientPriceList";
import ClientShippingRates from "@/components/ClientShippingRates";
import { DMYDateInput } from "@/components/ui/dmy-date-picker";
import { formatDMY } from "@/lib/dmy";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LedgerPage = () => {
  usePageTitle("Ledger");
  const { token } = useAuth();
  const [viewMode, setViewMode] = useState("table"); // "table" or "cards"
  const [ledgers, setLedgers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showHiddenColumns, setShowHiddenColumns] = useState(false);
  const [ratioEdit, setRatioEdit] = useState(null); // {ledger, r1, r2, r3, saving}
  const [aliasCleanupOpen, setAliasCleanupOpen] = useState(false);
  const [aliasScan, setAliasScan] = useState({ loading: false, candidates: [] });
  const [aliasSelected, setAliasSelected] = useState({});
  const [aliasMerging, setAliasMerging] = useState(false);
  const [roleReviewOpen, setRoleReviewOpen] = useState(false);
  const [roleReview, setRoleReview] = useState({ loading: false, items: [] });
  const [roleReviewSaving, setRoleReviewSaving] = useState("");

  const openRoleReview = async () => {
    setRoleReviewOpen(true);
    setRoleReview({ loading: true, items: [] });
    try {
      const res = await fetch(`${BACKEND_URL}/api/ledger-role-review`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await res.json();
      setRoleReview({ loading: false, items: d.items || [] });
    } catch {
      setRoleReview({ loading: false, items: [] });
      toast.error("Failed to load role review queue");
    }
  };

  const confirmRoles = async (assignments) => {
    if (!assignments.length) return;
    setRoleReviewSaving(assignments.length === 1 ? assignments[0].client_code : "__all__");
    try {
      const res = await fetch(`${BACKEND_URL}/api/ledger-role-review/confirm`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ assignments }),
      });
      const d = await res.json();
      if (!res.ok) { toast.error(typeof d.detail === "string" ? d.detail : "Confirm failed"); return; }
      toast.success(d.message);
      const done = new Set((d.confirmed || []).map((c) => c.client_code));
      setRoleReview((p) => ({ ...p, items: p.items.filter((i) => !done.has(i.client_code)) }));
    } catch (e) {
      toast.error("Network error: " + e.message);
    } finally {
      setRoleReviewSaving("");
    }
  };

  const openAliasCleanup = async () => {
    setAliasCleanupOpen(true);
    setAliasScan({ loading: true, candidates: [] });
    try {
      const res = await fetch(`${BACKEND_URL}/api/client-aliases/junk-scan`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await res.json();
      const cands = d.candidates || [];
      setAliasScan({ loading: false, candidates: cands });
      setAliasSelected(Object.fromEntries(cands.map((c) => [c.alias, true])));
    } catch {
      setAliasScan({ loading: false, candidates: [] });
      toast.error("Scan failed");
    }
  };

  const mergeAliases = async () => {
    const mappings = aliasScan.candidates.filter((c) => aliasSelected[c.alias]).map((c) => ({ alias: c.alias, canonical: c.canonical }));
    if (mappings.length === 0) return toast.error("Nothing selected");
    setAliasMerging(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/client-aliases/junk-merge`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ mappings }),
      });
      const d = await res.json();
      if (!res.ok) { toast.error(typeof d.detail === "string" ? d.detail : "Merge failed"); return; }
      toast.success(d.message);
      setAliasCleanupOpen(false);
      fetchLedgers();
    } catch (e) {
      toast.error("Network error: " + e.message);
    } finally {
      setAliasMerging(false);
    }
  };

  const saveRatioEdit = async () => {
    if (!ratioEdit || ratioEdit.saving) return;
    const clean = (v) => {
      const s = String(v).trim();
      if (s === "") return "";
      const n = parseFloat(s);
      return isNaN(n) || n < 0 || n > 100 ? null : String(n);
    };
    const r1 = clean(ratioEdit.r1), r2 = clean(ratioEdit.r2), r3 = clean(ratioEdit.r3);
    if (r1 === null || r2 === null || r3 === null) {
      toast.error("Ratios must be numbers between 0 and 100");
      return;
    }
    setRatioEdit((p) => ({ ...p, saving: true }));
    try {
      const l = ratioEdit.ledger;
      const res = await fetch(`${BACKEND_URL}/api/ledgers`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...l, profit_ratio_1: r1, profit_ratio_2: r2, profit_ratio_3: r3 }),
      });
      const d = await res.json();
      if (!res.ok) { toast.error(d.detail || "Failed to update ratios"); return; }
      toast.success(`${l.client_code} profit shares updated: ${r1 || "—"}/${r2 || "—"}/${r3 || "—"}`);
      setRatioEdit(null);
      fetchLedgers();
    } catch {
      toast.error("Network error while saving ratios");
    } finally {
      setRatioEdit((p) => (p ? { ...p, saving: false } : null));
    }
  };
  const [highlightedTracking, setHighlightedTracking] = useState(null); // Track searched tracking to highlight
  
  // Sorting state - Excel-like sorting
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  
  // Filter states
  const [showFilters, setShowFilters] = useState(false);
  const [saleTeamFilter, setSaleTeamFilter] = useState("all"); // Quick filter: "all", "dp_client", "partner", "agent"
  const [underQuickFilter, setUnderQuickFilter] = useState("all"); // Quick filter for manager
  const [autoUnclassifiedFilter, setAutoUnclassifiedFilter] = useState(false); // Quick pill: auto-created & still partner role
  const [healthBatch, setHealthBatch] = useState({}); // client_code -> { reship_free, reship_paid }
  const [filters, setFilters] = useState({
    ledgerType: "all", // all, Debtors/Customers, Supplier/Creditor
    role: "all", // all, Client, Partner, Agent, etc.
    under: "all", // all, or specific partner code
    balanceStatus: "all", // all, debit_only, credit_only, zero_balance, positive, negative
    agreement: "all", // all, Yes, No
    hasOrders: "all", // all, with_orders, without_orders
    minDebit: "",
    maxDebit: "",
    minCredit: "",
    maxCredit: "",
    minBalance: "",
    maxBalance: "",
    dateFrom: "", // Date filter - from date
    dateTo: "", // Date filter - to date
  });
  
  // Dialog states
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingLedger, setEditingLedger] = useState(null);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentData, setPaymentData] = useState({ client_code: "", amount: "", payment_date: "", payment_mode: "", reference_no: "", remarks: "" });
  const [showPaymentsListDialog, setShowPaymentsListDialog] = useState(false);
  const [paymentsList, setPaymentsList] = useState([]);
  const [paymentsClient, setPaymentsClient] = useState("");
  // Debit dialog states
  const [showDebitDialog, setShowDebitDialog] = useState(false);
  const [debitData, setDebitData] = useState({ client_code: "", amount: "", debit_date: "", debit_type: "", reference_no: "", remarks: "" });
  const [showDebitsListDialog, setShowDebitsListDialog] = useState(false);
  const [debitsList, setDebitsList] = useState([]);
  const [debitsClient, setDebitsClient] = useState("");
  const [showPriceListDialog, setShowPriceListDialog] = useState(false);
  const [priceListClient, setPriceListClient] = useState(null);
  const [showShippingRatesDialog, setShowShippingRatesDialog] = useState(false);
  const [shippingRatesClient, setShippingRatesClient] = useState(null);
  
  // Detail view states
  const [selectedLedger, setSelectedLedger] = useState(null);
  const [ledgerDetails, setLedgerDetails] = useState(null);
  const [summaryView, setSummaryView] = useState("year");
  const [summaryData, setSummaryData] = useState([]);
  const [grandTotal, setGrandTotal] = useState({ total_debit: 0, total_credit: 0, total_collection: 0, total_balance: 0, total_orders: 0 });
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [dayDetails, setDayDetails] = useState(null);
  
  // Table grand total
  const [tableGrandTotal, setTableGrandTotal] = useState({ total_debit: 0, total_credit: 0, total_balance: 0 });
  
  // Unassigned orders state
  const [ordersWithoutClient, setOrdersWithoutClient] = useState(0);
  const [totalParcels, setTotalParcels] = useState(0);
  const [showUnassignedDialog, setShowUnassignedDialog] = useState(false);
  const [unassignedOrders, setUnassignedOrders] = useState([]);
  const [unassignedLoading, setUnassignedLoading] = useState(false);
  const [unassignedPage, setUnassignedPage] = useState(1);
  const [unassignedTotal, setUnassignedTotal] = useState(0);
  const [unassignedTotalPages, setUnassignedTotalPages] = useState(0);
  const [availableClientCodes, setAvailableClientCodes] = useState([]);
  const [selectedUnassignedOrders, setSelectedUnassignedOrders] = useState([]);
  const [assignClientCode, setAssignClientCode] = useState("");
  const [unassignedSearch, setUnassignedSearch] = useState("");
  
  // Client Alias Management state
  const [showAliasDialog, setShowAliasDialog] = useState(false);
  const [aliases, setAliases] = useState([]);
  const [aliasesLoading, setAliasesLoading] = useState(false);
  const [newAlias, setNewAlias] = useState({ alias: "", canonical_code: "" });
  const [duplicates, setDuplicates] = useState([]);
  const [duplicatesLoading, setDuplicatesLoading] = useState(false);
  const [showMergeDialog, setShowMergeDialog] = useState(false);
  const [mergeData, setMergeData] = useState({ source_codes: [], target_code: "" });
  const [aliasTab, setAliasTab] = useState("aliases"); // "aliases", "duplicates", "suggestions", "history"
  const [aliasFixResult, setAliasFixResult] = useState(null);
  // Aliases Health Check
  const [aliasHealth, setAliasHealth] = useState(null);
  const [aliasHealthLoading, setAliasHealthLoading] = useState(false);
  const [aliasFixLoading, setAliasFixLoading] = useState(false);
  const [aliasSuggestions, setAliasSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [aliasHistory, setAliasHistory] = useState([]);
  const [aliasHistoryLoading, setAliasHistoryLoading] = useState(false);
  const [undoPrompt, setUndoPrompt] = useState(null); // {id, alias, canonical_code}
  const [undoPassword, setUndoPassword] = useState("");
  const [undoLoading, setUndoLoading] = useState(false);
  
  // Sorting handler - Excel-like behavior
  const handleSort = (key) => {
    setSortConfig(prev => {
      if (prev.key === key) {
        // Cycle: asc -> desc -> none
        if (prev.direction === 'asc') return { key, direction: 'desc' };
        if (prev.direction === 'desc') return { key: null, direction: null };
      }
      return { key, direction: 'asc' };
    });
  };
  
  // Get sort icon for column header
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-40" />;
    if (sortConfig.direction === 'asc') return <ArrowUp className="w-3 h-3 ml-1 text-indigo-600" />;
    if (sortConfig.direction === 'desc') return <ArrowDown className="w-3 h-3 ml-1 text-indigo-600" />;
    return <ArrowUpDown className="w-3 h-3 ml-1 opacity-40" />;
  };
  
  // Reset all filters
  const resetFilters = () => {
    setSaleTeamFilter("all");
    setUnderQuickFilter("all");
    setAutoUnclassifiedFilter(false);
    setFilters({
      ledgerType: "all",
      role: "all",
      under: "all",
      balanceStatus: "all",
      agreement: "all",
      hasOrders: "all",
      minDebit: "",
      maxDebit: "",
      minCredit: "",
      maxCredit: "",
      minBalance: "",
      maxBalance: "",
      dateFrom: "",
      dateTo: "",
    });
    setSortConfig({ key: null, direction: null });
  };
  
  // Check if any filter is active
  const hasActiveFilters = useMemo(() => {
    return saleTeamFilter !== "all" ||
           underQuickFilter !== "all" ||
           autoUnclassifiedFilter ||
           filters.ledgerType !== "all" ||
           filters.role !== "all" ||
           filters.under !== "all" ||
           filters.balanceStatus !== "all" ||
           filters.agreement !== "all" ||
           filters.hasOrders !== "all" ||
           filters.minDebit !== "" ||
           filters.maxDebit !== "" ||
           filters.minCredit !== "" ||
           filters.maxCredit !== "" ||
           filters.minBalance !== "" ||
           filters.maxBalance !== "" ||
           filters.dateFrom !== "" ||
           filters.dateTo !== "";
  }, [filters, saleTeamFilter, underQuickFilter, autoUnclassifiedFilter]);

  // Fetch ledgers with debit/credit/balance
  const fetchLedgers = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/ledgers-table`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setLedgers(data.ledgers || []);
      setTableGrandTotal(data.grand_total || { total_debit: 0, total_credit: 0, total_balance: 0 });
      setOrdersWithoutClient(data.orders_without_client || 0);
      setTotalParcels(data.total_parcels || 0);
    } catch (error) {
      toast.error("Failed to load ledgers");
    } finally {
      setLoading(false);
    }
  };

  // Fetch batch health snapshot (used to render the compact issue pill per row)
  const fetchHealthBatch = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/reports/client-health-batch`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setHealthBatch(data?.clients || {});
    } catch {
      /* silent — the pill just doesn't render */
    }
  };

  // Fetch unassigned orders
  const fetchUnassignedOrders = async (page = 1, search = "") => {
    try {
      setUnassignedLoading(true);
      const params = new URLSearchParams({ page, limit: 50 });
      if (search) params.append("search", search);
      
      const response = await fetch(`${BACKEND_URL}/api/ledgers/unassigned-orders?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setUnassignedOrders(data.orders || []);
      setUnassignedTotal(data.total || 0);
      setUnassignedTotalPages(data.total_pages || 0);
      setUnassignedPage(page);
      setAvailableClientCodes(data.client_codes || []);
    } catch (error) {
      toast.error("Failed to load unassigned orders");
    } finally {
      setUnassignedLoading(false);
    }
  };

  // Assign client code to selected orders
  const handleAssignClientCode = async () => {
    if (!assignClientCode) {
      toast.error("Please select a client code");
      return;
    }
    if (selectedUnassignedOrders.length === 0) {
      toast.error("Please select at least one order");
      return;
    }
    
    try {
      const trackingNumbers = selectedUnassignedOrders.map(o => o.tracking).filter(Boolean);
      const response = await fetch(`${BACKEND_URL}/api/ledgers/assign-client-code`, {
        method: "POST",
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          tracking_numbers: trackingNumbers,
          client_code: assignClientCode
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to assign client code");
      }
      
      const data = await response.json();
      toast.success(data.message);
      
      // Refresh data
      setSelectedUnassignedOrders([]);
      setAssignClientCode("");
      fetchUnassignedOrders(unassignedPage, unassignedSearch);
      fetchLedgers();
    } catch (error) {
      toast.error(error.message || "Failed to assign client code");
    }
  };

  // Auto-generate ledgers from orders
  const autoGenerateLedgers = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/ledgers-auto-generate`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      toast.success(data.message);
      fetchLedgers();
    } catch (error) {
      toast.error("Failed to auto-generate ledgers");
    }
  };

  // Fetch account summary with drill-down
  const fetchAccountSummary = async (clientCode, view, year = null, month = null) => {
    try {
      let url = `${BACKEND_URL}/api/ledgers/${clientCode}/account-summary?view=${view}`;
      if (year) url += `&year=${year}`;
      if (month) url += `&month=${month}`;
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setSummaryData(data.summary || []);
      setGrandTotal(data.grand_total || { total_debit: 0, total_credit: 0, total_balance: 0 });
    } catch (error) {
      toast.error("Failed to load summary");
    }
  };

  // Fetch day details
  const fetchDayDetails = async (clientCode, date) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/ledgers/${clientCode}/day-details?date=${date}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setDayDetails(data);
    } catch (error) {
      toast.error("Failed to load day details");
    }
  };

  // Fetch payments for a client
  const fetchPayments = async (clientCode) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/ledger-payments/${clientCode}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setPaymentsList(data.payments || []);
      setPaymentsClient(clientCode);
      setShowPaymentsListDialog(true);
    } catch (error) {
      toast.error("Failed to load payments");
    }
  };

  // Save ledger
  const saveLedger = async () => {
    if (!editingLedger?.client_code) {
      toast.error("Client code is required");
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/ledgers`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(editingLedger)
      });

      const data = await response.json();
      if (response.ok) {
        toast.success(data.message);
        setShowEditDialog(false);
        setEditingLedger(null);
        fetchLedgers();
      } else {
        toast.error(data.detail || "Failed to save");
      }
    } catch (error) {
      toast.error("Failed to save ledger");
    }
  };

  // Save payment
  const savePayment = async () => {
    if (!paymentData.client_code || !paymentData.amount) {
      toast.error("Client code and amount are required");
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/ledger-payments`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(paymentData)
      });

      const data = await response.json();
      if (response.ok) {
        toast.success(data.message);
        setShowPaymentDialog(false);
        setPaymentData({ client_code: "", amount: "", payment_date: "", payment_mode: "", reference_no: "", remarks: "" });
        fetchLedgers();
        if (selectedLedger) {
          fetchAccountSummary(selectedLedger, summaryView, selectedYear, selectedMonth);
        }
      } else {
        toast.error(data.detail || "Failed to add payment");
      }
    } catch (error) {
      toast.error("Failed to add payment");
    }
  };

  // Save debit entry
  const saveDebit = async () => {
    if (!debitData.client_code || !debitData.amount) {
      toast.error("Client code and amount are required");
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/ledger-debits`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(debitData)
      });

      const data = await response.json();
      if (response.ok) {
        toast.success(data.message);
        setShowDebitDialog(false);
        setDebitData({ client_code: "", amount: "", debit_date: "", debit_type: "", reference_no: "", remarks: "" });
        fetchLedgers();
        if (selectedLedger) {
          fetchAccountSummary(selectedLedger, summaryView, selectedYear, selectedMonth);
        }
      } else {
        toast.error(data.detail || "Failed to add debit");
      }
    } catch (error) {
      toast.error("Failed to add debit");
    }
  };

  // Fetch debits for a client
  const fetchDebits = async (clientCode) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/ledger-debits/${clientCode}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        setDebitsList(data.debits || []);
        setDebitsClient(clientCode);
        setShowDebitsListDialog(true);
      }
    } catch (error) {
      toast.error("Failed to fetch debits");
    }
  };

  // Delete debit entry
  const deleteDebit = async (debitId) => {
    if (!window.confirm("Delete this debit entry?")) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/ledger-debits/${debitId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success("Debit entry deleted");
        fetchDebits(debitsClient);
        fetchLedgers();
      } else {
        toast.error("Failed to delete debit");
      }
    } catch (error) {
      toast.error("Failed to delete debit");
    }
  };

  // Delete ledger
  const deleteLedger = async (clientCode) => {
    if (!window.confirm(`Delete ledger for ${clientCode}?\n\nThis will also remove any aliases pointing to it and any payment follow-up rows for this partner. Shipment orders are kept.`)) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/ledgers/${encodeURIComponent(clientCode)}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        const c = data.cascade || {};
        const parts = [];
        if (c.payment_followups_dropped) parts.push(`${c.payment_followups_dropped} follow-ups`);
        if (c.client_aliases_dropped) parts.push(`${c.client_aliases_dropped} aliases`);
        const cascadeMsg = parts.length ? ` · cleaned ${parts.join(", ")}` : "";
        toast.success(`Ledger deleted${cascadeMsg}`);
        fetchLedgers();
        if (selectedLedger === clientCode) {
          setSelectedLedger(null);
          setLedgerDetails(null);
        }
      } else {
        toast.error(data.detail || "Failed to delete");
      }
    } catch (error) {
      toast.error("Failed to delete ledger");
    }
  };

  // Select a ledger to view details
  const selectLedger = (clientCode) => {
    setSelectedLedger(clientCode);
    setSummaryView("year");
    setSelectedYear(null);
    setSelectedMonth(null);
    setDayDetails(null);
    fetchAccountSummary(clientCode, "year");
  };

  // Handle period click (drill down)
  const handlePeriodClick = (period) => {
    if (summaryView === "year") {
      setSelectedYear(parseInt(period));
      setSummaryView("month");
      fetchAccountSummary(selectedLedger, "month", parseInt(period));
    } else if (summaryView === "month") {
      const [year, month] = period.split("-");
      setSelectedMonth(parseInt(month));
      setSummaryView("day");
      fetchAccountSummary(selectedLedger, "day", parseInt(year), parseInt(month));
    } else if (summaryView === "day") {
      fetchDayDetails(selectedLedger, period);
    }
  };

  // Go back in drill-down
  const goBack = () => {
    if (dayDetails) {
      setDayDetails(null);
    } else if (summaryView === "day") {
      setSummaryView("month");
      setSelectedMonth(null);
      fetchAccountSummary(selectedLedger, "month", selectedYear);
    } else if (summaryView === "month") {
      setSummaryView("year");
      setSelectedYear(null);
      fetchAccountSummary(selectedLedger, "year");
    } else {
      setSelectedLedger(null);
      setLedgerDetails(null);
      setSummaryData([]);
      setDayDetails(null);
      setHighlightedTracking(null); // Clear highlighted tracking when going back to main view
    }
  };

  // Handle double-click to add credit
  const handleDoubleClick = (ledger) => {
    setPaymentData({
      client_code: ledger.client_code,
      amount: "",
      payment_date: new Date().toISOString().split("T")[0],
      payment_mode: "",
      reference_no: "",
      remarks: ""
    });
    setShowPaymentDialog(true);
  };

  // Fetch client aliases
  const fetchAliases = async () => {
    setAliasesLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/client-aliases`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        setAliases(data.aliases || []);
      }
    } catch (error) {
      console.error("Error fetching aliases:", error);
      toast.error("Failed to load aliases");
    } finally {
      setAliasesLoading(false);
    }
  };

  // Run the alias drift health-check (per-alias / per-collection counts).
  const fetchAliasHealth = async () => {
    setAliasHealthLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/client-aliases/health-check`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      if (r.ok) {
        setAliasHealth(d);
        if (d.total_drifted_rows === 0) {
          toast.success(`Health check OK — all ${d.total_aliases} aliases fully cascaded`);
        } else {
          toast.info(`${d.total_drifted_rows.toLocaleString()} row(s) drifted across ${d.total_aliases - d.alias_count_clean} aliases — click "Run cascade" to fix`);
        }
      } else {
        toast.error(d.detail || "Health check failed");
      }
    } catch (e) {
      toast.error(`Health check failed: ${e.message}`);
    } finally {
      setAliasHealthLoading(false);
    }
  };

  // Re-run the full cascade for every alias (also covers raw_funds_rows now)
  const runAliasCascadeAll = async () => {
    setAliasHealthLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/client-aliases/run-cascade-all`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      if (r.ok) {
        toast.success(`Cascade run — ${d.total_aliases || aliases.length} aliases reprocessed`);
        await fetchAliasHealth();
      } else {
        toast.error(d.detail || "Cascade failed");
      }
    } catch (e) {
      toast.error(`Cascade failed: ${e.message}`);
    } finally {
      setAliasHealthLoading(false);
    }
  };

  // Fetch duplicate clients
  const fetchDuplicates = async () => {
    setDuplicatesLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/clients/duplicates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        setDuplicates(data.duplicates || []);
      }
    } catch (error) {
      console.error("Error fetching duplicates:", error);
      toast.error("Failed to load duplicates");
    } finally {
      setDuplicatesLoading(false);
    }
  };

  // Create new alias
  const createAlias = async () => {
    if (!newAlias.alias || !newAlias.canonical_code) {
      toast.error("Both alias and target client code are required");
      return;
    }
    try {
      const response = await fetch(`${BACKEND_URL}/api/client-aliases`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify(newAlias)
      });
      const data = await response.json();
      if (response.ok) {
        const c = data.cascade || {};
        const parts = [];
        if (c.payment_followups) parts.push(`${c.payment_followups} follow-ups`);
        if (c.shipment_orders) parts.push(`${c.shipment_orders} orders`);
        if (c.sales) parts.push(`${c.sales} sales`);
        if (c.ledger_entries) parts.push(`${c.ledger_entries} ledger entries`);
        if (c.client_price_lists) parts.push(`${c.client_price_lists} price lists`);
        if (c.alias_ledger_dropped) parts.push("alias ledger removed");
        const cascadeMsg = parts.length ? ` · merged ${parts.join(", ")}` : "";
        toast.success(`Alias created: ${newAlias.alias} → ${newAlias.canonical_code}${cascadeMsg}`);
        setNewAlias({ alias: "", canonical_code: "" });
        fetchAliases();
      } else {
        toast.error(data.detail || "Failed to create alias");
      }
    } catch (error) {
      console.error("Error creating alias:", error);
      toast.error("Failed to create alias");
    }
  };

  // Delete alias
  const deleteAlias = async (alias) => {
    if (!window.confirm(`Delete alias "${alias}"?`)) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/client-aliases/${encodeURIComponent(alias)}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success(`Alias "${alias}" deleted`);
        fetchAliases();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to delete alias");
      }
    } catch (error) {
      console.error("Error deleting alias:", error);
      toast.error("Failed to delete alias");
    }
  };

  // Fix historical data by applying aliases to all collections
  const fixHistoricalAliases = async (dryRun = true) => {
    setAliasFixLoading(true);
    setAliasFixResult(null);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/fix-client-aliases`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ dry_run: dryRun })
      });
      const data = await response.json();
      if (response.ok) {
        setAliasFixResult(data);
        if (!dryRun && data.total_records_affected > 0) {
          toast.success(`Updated ${data.total_records_affected} records!`);
          fetchLedgers(); // Refresh ledger data
        } else if (data.total_records_affected === 0) {
          toast.info("No records need to be updated");
        }
      } else {
        toast.error(data.detail || "Failed to fix aliases");
      }
    } catch (error) {
      console.error("Error fixing aliases:", error);
      toast.error("Failed to fix aliases");
    } finally {
      setAliasFixLoading(false);
    }
  };

  // Merge duplicate clients
  const mergeDuplicates = async () => {
    if (!mergeData.target_code || mergeData.source_codes.length === 0) {
      toast.error("Please select source codes and target code");
      return;
    }
    try {
      const response = await fetch(`${BACKEND_URL}/api/clients/merge`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({
          source_codes: mergeData.source_codes,
          target_code: mergeData.target_code,
          create_aliases: true
        })
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message);
        setShowMergeDialog(false);
        setMergeData({ source_codes: [], target_code: "" });
        fetchDuplicates();
        fetchAliases();
        fetchLedgers();
      } else {
        toast.error(data.detail || "Failed to merge clients");
      }
    } catch (error) {
      console.error("Error merging clients:", error);
      toast.error("Failed to merge clients");
    }
  };

  // Fetch alias suggestions
  const fetchAliasSuggestions = async () => {
    setSuggestionsLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/client-aliases/suggest`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        setAliasSuggestions(data.suggestions || []);
      } else {
        toast.error(data.detail || "Failed to fetch suggestions");
      }
    } catch (error) {
      console.error("Error fetching suggestions:", error);
    } finally {
      setSuggestionsLoading(false);
    }
  };

  // Create alias from suggestion
  const createAliasFromSuggestion = async (suggestion) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/client-aliases`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ 
          alias: suggestion.alias, 
          canonical_code: suggestion.canonical_code 
        })
      });
      if (response.ok) {
        toast.success(`Alias created: ${suggestion.alias} → ${suggestion.canonical_code}`);
        fetchAliases();
        // Remove from suggestions
        setAliasSuggestions(prev => prev.filter(s => s.alias !== suggestion.alias));
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to create alias");
      }
    } catch (error) {
      console.error("Error creating alias:", error);
      toast.error("Failed to create alias");
    }
  };

  // Open alias dialog and load data
  const openAliasDialog = () => {
    setShowAliasDialog(true);
    fetchAliases();
    fetchDuplicates();
    fetchAliasSuggestions();
  };

  useEffect(() => {
    fetchLedgers();
    fetchHealthBatch();
  }, [token]);

  // Filter ledgers by search and apply filters + sorting
  const filteredLedgers = useMemo(() => {
    let result = [...ledgers];
    
    // Text search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(l => 
        l.client_code?.toLowerCase().includes(term) ||
        l.name?.toLowerCase().includes(term) ||
        l.ledger_type?.toLowerCase().includes(term) ||
        l.role?.toLowerCase().includes(term)
      );
    }
    
    // Sale Team quick filter (groups multiple role values)
    if (saleTeamFilter !== "all") {
      const DP_CLIENT_ROLES = ["client", "dp client", "dp clients"];
      if (saleTeamFilter === "dp_client") {
        result = result.filter(l => DP_CLIENT_ROLES.includes((l.role || "").toLowerCase()));
      } else if (saleTeamFilter === "partner") {
        result = result.filter(l => (l.role || "").toLowerCase() === "partner");
      } else if (saleTeamFilter === "agent") {
        result = result.filter(l => (l.role || "").toLowerCase() === "agent");
      }
    }

    // Under Manager quick filter
    if (underQuickFilter !== "all") {
      result = result.filter(l => l.under === underQuickFilter);
    }

    // Auto-created (unclassified) quick pill - shows ledgers created during upload
    // that are still on the default "partner" role and need reclassification.
    if (autoUnclassifiedFilter) {
      result = result.filter(l =>
        l.auto_created === true && (l.role || "").toLowerCase() === "partner"
      );
    }

    // Ledger Type filter (Clients/Partners)
    if (filters.ledgerType !== "all") {
      result = result.filter(l => l.ledger_type === filters.ledgerType);
    }
    
    // Role filter
    if (filters.role !== "all") {
      result = result.filter(l => l.role === filters.role);
    }
    
    // Under filter (who is handling the customer)
    if (filters.under !== "all") {
      result = result.filter(l => l.under === filters.under);
    }
    
    // Balance Status filter
    if (filters.balanceStatus !== "all") {
      switch (filters.balanceStatus) {
        case "debit_only":
          result = result.filter(l => (l.debit || 0) > 0 && (l.credit || 0) === 0);
          break;
        case "credit_only":
          result = result.filter(l => (l.credit || 0) > 0 && (l.debit || 0) === 0);
          break;
        case "zero_balance":
          result = result.filter(l => Math.abs(l.balance || 0) < 0.01);
          break;
        case "positive":
          result = result.filter(l => (l.balance || 0) > 0);
          break;
        case "negative":
          result = result.filter(l => (l.balance || 0) < 0);
          break;
        default:
          break;
      }
    }
    
    // Agreement filter
    if (filters.agreement !== "all") {
      result = result.filter(l => l.agreement === filters.agreement);
    }
    
    // Has Orders filter
    if (filters.hasOrders !== "all") {
      if (filters.hasOrders === "with_orders") {
        result = result.filter(l => (l.order_count || 0) > 0);
      } else if (filters.hasOrders === "without_orders") {
        result = result.filter(l => (l.order_count || 0) === 0);
      }
    }
    
    // Amount range filters
    if (filters.minDebit !== "") {
      result = result.filter(l => (l.debit || 0) >= parseFloat(filters.minDebit));
    }
    if (filters.maxDebit !== "") {
      result = result.filter(l => (l.debit || 0) <= parseFloat(filters.maxDebit));
    }
    if (filters.minCredit !== "") {
      result = result.filter(l => (l.credit || 0) >= parseFloat(filters.minCredit));
    }
    if (filters.maxCredit !== "") {
      result = result.filter(l => (l.credit || 0) <= parseFloat(filters.maxCredit));
    }
    if (filters.minBalance !== "") {
      result = result.filter(l => (l.balance || 0) >= parseFloat(filters.minBalance));
    }
    if (filters.maxBalance !== "") {
      result = result.filter(l => (l.balance || 0) <= parseFloat(filters.maxBalance));
    }
    
    // Date range filter (based on join_date)
    if (filters.dateFrom !== "") {
      const fromDate = new Date(filters.dateFrom);
      result = result.filter(l => {
        if (!l.join_date) return false;
        const joinDate = new Date(l.join_date);
        return joinDate >= fromDate;
      });
    }
    if (filters.dateTo !== "") {
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59, 999); // Include the entire day
      result = result.filter(l => {
        if (!l.join_date) return false;
        const joinDate = new Date(l.join_date);
        return joinDate <= toDate;
      });
    }
    
    // Apply sorting
    if (sortConfig.key && sortConfig.direction) {
      result.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];
        
        // Handle null/undefined
        if (aVal == null) aVal = '';
        if (bVal == null) bVal = '';
        
        // Numeric columns
        const numericKeys = ['debit', 'credit', 'balance', 'order_count'];
        if (numericKeys.includes(sortConfig.key)) {
          aVal = parseFloat(aVal) || 0;
          bVal = parseFloat(bVal) || 0;
        } else {
          // String comparison
          aVal = String(aVal).toLowerCase();
          bVal = String(bVal).toLowerCase();
        }
        
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    return result;
  }, [ledgers, searchTerm, saleTeamFilter, underQuickFilter, autoUnclassifiedFilter, filters, sortConfig]);

  // Count of auto-created ledgers still on default "partner" role (need reclassification)
  const autoUnclassifiedCount = useMemo(() => {
    return (ledgers || []).filter(l =>
      l.auto_created === true && (l.role || "").toLowerCase() === "partner"
    ).length;
  }, [ledgers]);
  
  // Calculate filtered totals
  const filteredTotals = useMemo(() => {
    const totals = filteredLedgers.reduce((acc, l) => ({
      total_debit: acc.total_debit + (l.debit || 0),
      total_credit: acc.total_credit + (l.credit || 0),
      total_balance: acc.total_balance + (l.balance || 0),
      total_orders: acc.total_orders + (l.order_count || 0),
    }), { total_debit: 0, total_credit: 0, total_balance: 0, total_orders: 0 });
    return totals;
  }, [filteredLedgers]);
  
  // Get unique roles for filter dropdown
  const uniqueRoles = useMemo(() => {
    const roles = new Set(ledgers.map(l => l.role).filter(Boolean));
    return Array.from(roles).sort();
  }, [ledgers]);
  
  // Get unique "Under" values for filter dropdown
  const uniqueUnder = useMemo(() => {
    const underValues = new Set(ledgers.map(l => l.under).filter(Boolean));
    return Array.from(underValues).sort();
  }, [ledgers]);

  // Format currency
  const formatCurrency = (value) => {
    if (!value && value !== 0) return "₹0.0";
    return `₹${parseFloat(value).toLocaleString('en-IN', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}`;
  };

  // Format period for display
  const formatPeriod = (period, view) => {
    if (view === "year") return period;
    if (view === "month") {
      const [year, month] = period.split("-");
      const monthNames = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      return `${monthNames[parseInt(month)]} ${year}`;
    }
    if (view === "day") {
      const [year, month, day] = period.split("-");
      return `${day}/${month}/${year}`;
    }
    return period;
  };

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          {selectedLedger && (
            <Button variant="outline" size="sm" onClick={goBack} className="mr-2 gap-1 border-slate-300 hover:bg-slate-100">
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">
                {dayDetails ? "Day View" : 
                 summaryView === "day" ? "Month View" :
                 summaryView === "month" ? "Year View" :
                 "Sales Team"}
              </span>
            </Button>
          )}
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-indigo-500/25">
            <BookOpen className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-bold text-foreground">
              {selectedLedger ? `Ledger (Costing): ${selectedLedger}` : "Ledger (Costing)"}
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              {selectedLedger 
                ? `${dayDetails ? "Day Details" : summaryView === "year" ? "Year-wise" : summaryView === "month" ? "Month-wise" : "Day-wise"} Account Summary`
                : `${filteredLedgers.length}${filteredLedgers.length !== ledgers.length ? ` of ${ledgers.length}` : ''} clients | Balance: ${formatCurrency(filteredTotals.total_balance)}`
              }
            </p>
          </div>
        </div>

        {!selectedLedger && (
          <div className="flex flex-wrap gap-2">
            <Button
              variant={viewMode === "table" ? "default" : "outline"}
              size="sm"
              onClick={() => setViewMode("table")}
              className="gap-2"
            >
              <Table className="w-4 h-4" />
              Table
            </Button>
            <Button
              variant={viewMode === "cards" ? "default" : "outline"}
              size="sm"
              onClick={() => setViewMode("cards")}
              className="gap-2"
            >
              <LayoutGrid className="w-4 h-4" />
              Cards
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowHiddenColumns(!showHiddenColumns)}
              className={`gap-2 ${showHiddenColumns ? 'bg-blue-50 border-blue-300' : ''}`}
              title="Phone, GST, IEC, Website, Agreement, Buy Leads, Join Date"
            >
              {showHiddenColumns ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              {showHiddenColumns ? "Hide Details" : "Show Details"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={openAliasCleanup}
              className="gap-2 border-amber-300 text-amber-700 hover:bg-amber-50"
              title="Scan for junk-charactered client names and merge them into their real partner"
              data-testid="alias-cleanup-btn"
            >
              <Eraser className="w-4 h-4" />
              Alias Cleanup
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={openRoleReview}
              className="gap-2 border-sky-300 text-sky-700 hover:bg-sky-50"
              title="Review auto-created clients whose role was never confirmed — classify them as Partner / Agent / DP Client in one click"
              data-testid="role-review-btn"
            >
              <UserCheck className="w-4 h-4" />
              Role Review
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={autoGenerateLedgers}
              className="gap-2 border-purple-300 hover:bg-purple-50"
            >
              <Zap className="w-4 h-4 text-purple-500" />
              Auto Generate
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={openAliasDialog}
              className="gap-2 border-amber-300 hover:bg-amber-50"
              title="Manage client aliases and merge duplicates"
            >
              <List className="w-4 h-4 text-amber-500" />
              Account Aliases
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setEditingLedger({
                  client_code: "",
                  ledger_type: "Debtors/Customers",
                  role: "Client",
                  under: "",
                  name: "",
                  phone_number: "",
                  gst_number: "",
                  iec_code: "",
                  website_name: "",
                  agreement: "No",
                  can_view_hidden_products: false,
                  buy_leads: "",
                  catalog_date_from: "",
                  catalog_date_to: "",
                  account_details: "",
                  address: "",
                  contact: "",
                  email: "",
                  join_date: "",
                  profit_share_1_name: "Self",
                  profit_ratio_1: "",
                  profit_share_2_name: "",
                  profit_ratio_2: "",
                  profit_share_3_name: "",
                  profit_ratio_3: "",
                  notes: ""
                });
                setShowEditDialog(true);
              }}
              className="gap-2 border-blue-300 hover:bg-blue-50"
            >
              <Plus className="w-4 h-4 text-blue-500" />
              Add Ledger
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setPaymentData({ client_code: "", amount: "", payment_date: new Date().toISOString().split("T")[0], payment_mode: "", reference_no: "", remarks: "" });
                setShowPaymentDialog(true);
              }}
              className="gap-2 border-green-300 hover:bg-green-50"
            >
              <CreditCard className="w-4 h-4 text-green-500" />
              Add Payment
            </Button>
            <Button variant="outline" size="sm" onClick={fetchLedgers} className="gap-2">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>

      {/* Main Content */}
      {!selectedLedger ? (
        <>
          {/* Search and Filter Bar */}
          <div className="flex flex-col gap-3">
            {/* Quick Filters + Search Row */}
            <div className="flex items-center gap-2 flex-wrap">
              {/* Sale Team Quick Filter */}
              <Select value={saleTeamFilter} onValueChange={setSaleTeamFilter}>
                <SelectTrigger className="w-[140px] h-9 text-sm" data-testid="ledger-sale-team-filter">
                  <SelectValue placeholder="Sale Team" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all"><Users className="w-4 h-4 inline mr-2 text-slate-500" />All Teams</SelectItem>
                  <SelectItem value="dp_client"><Users className="w-4 h-4 inline mr-2 text-green-600" />DP Clients</SelectItem>
                  <SelectItem value="partner"><Building2 className="w-4 h-4 inline mr-2 text-purple-600" />Partners</SelectItem>
                  <SelectItem value="agent"><UserCheck className="w-4 h-4 inline mr-2 text-blue-600" />Agents</SelectItem>
                </SelectContent>
              </Select>

              {/* Under Manager Quick Filter */}
              <Select value={underQuickFilter} onValueChange={setUnderQuickFilter}>
                <SelectTrigger className="w-[140px] h-9 text-sm" data-testid="ledger-under-manager-filter">
                  <SelectValue placeholder="Manager" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all"><Briefcase className="w-4 h-4 inline mr-2 text-slate-500" />All Managers</SelectItem>
                  {uniqueUnder.map(m => (
                    <SelectItem key={m} value={m}>
                      <Briefcase className="w-4 h-4 inline mr-2 text-amber-600" />{m}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Auto-created (unclassified) Quick Pill - shows partner-role ledgers created during uploads */}
              {autoUnclassifiedCount > 0 && (
                <button
                  type="button"
                  onClick={() => setAutoUnclassifiedFilter(v => !v)}
                  data-testid="ledger-auto-unclassified-pill"
                  className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-sm font-medium border transition-colors ${
                    autoUnclassifiedFilter
                      ? "bg-amber-500 text-white border-amber-500 hover:bg-amber-600"
                      : "bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100"
                  }`}
                  title="Show ledgers auto-created during Excel uploads that are still on the default 'partner' role and need reclassification"
                >
                  <Zap className="w-3.5 h-3.5" />
                  Auto-created (unclassified)
                  <span className={`ml-1 text-xs px-1.5 py-0.5 rounded-full ${
                    autoUnclassifiedFilter ? "bg-white/25 text-white" : "bg-amber-200 text-amber-800"
                  }`}>
                    {autoUnclassifiedCount}
                  </span>
                </button>
              )}

              {/* Active quick-filter badges */}
              {(saleTeamFilter !== "all" || underQuickFilter !== "all") && (
                <div className="flex items-center gap-1">
                  {saleTeamFilter !== "all" && (
                    <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full ${
                      saleTeamFilter === "partner" ? "bg-purple-100 text-purple-700" :
                      saleTeamFilter === "agent" ? "bg-blue-100 text-blue-700" :
                      "bg-green-100 text-green-700"
                    }`}>
                      {saleTeamFilter === "dp_client" ? "DP Clients" : saleTeamFilter === "partner" ? "Partners" : "Agents"}
                      <button onClick={() => setSaleTeamFilter("all")} className="hover:opacity-70"><X className="w-3 h-3" /></button>
                    </span>
                  )}
                  {underQuickFilter !== "all" && (
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-700">
                      Under: {underQuickFilter}
                      <button onClick={() => setUnderQuickFilter("all")} className="hover:opacity-70"><X className="w-3 h-3" /></button>
                    </span>
                  )}
                </div>
              )}

              {/* Search - pushed to right */}
              <div className="relative flex-1 max-w-md ml-auto">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by client code, name, tracking number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={async (e) => {
                  // If user presses Enter and search term looks like a tracking number
                  if (e.key === 'Enter' && searchTerm.length >= 5) {
                    // Check if it might be a tracking number (contains letters and numbers)
                    const mightBeTracking = /^[A-Z]{2}\d+[A-Z]{2}$/i.test(searchTerm) || 
                                            /^[A-Z]+\d+/i.test(searchTerm) ||
                                            searchTerm.toUpperCase().startsWith('LP') ||
                                            searchTerm.toUpperCase().startsWith('EM') ||
                                            searchTerm.toUpperCase().startsWith('RM');
                    
                    if (mightBeTracking) {
                      try {
                        const response = await fetch(`${BACKEND_URL}/api/ledgers/search-tracking/${encodeURIComponent(searchTerm)}`, {
                          headers: { Authorization: `Bearer ${token}` }
                        });
                        const data = await response.json();
                        
                        if (data.found && data.client_code) {
                          // Found! Navigate to that client's ledger
                          toast.success(`Found tracking ${data.tracking} for client: ${data.client_code}`);
                          
                          // Save the tracking to highlight it in the results
                          setHighlightedTracking(data.tracking);
                          
                          // Use selectLedger function to properly load the ledger data
                          selectLedger(data.client_code);
                          
                          // If we have order_date, try to drill down to that specific date
                          if (data.order_date && data.order_date.length === 6) {
                            // order_date is in DDMMYY format, need to extract year
                            const day = data.order_date.substring(0, 2);
                            const month = data.order_date.substring(2, 4);
                            const year = "20" + data.order_date.substring(4, 6);
                            
                            // Set a timeout to allow the ledger to load first, then drill down
                            setTimeout(() => {
                              setSelectedYear(parseInt(year));
                              setSummaryView("month");
                              fetchAccountSummary(data.client_code, "month", parseInt(year));
                              
                              setTimeout(() => {
                                setSelectedMonth(parseInt(month));
                                setSummaryView("day");
                                fetchAccountSummary(data.client_code, "day", parseInt(year), parseInt(month));
                                
                                // Finally show day details
                                setTimeout(() => {
                                  const dateStr = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
                                  fetchDayDetails(data.client_code, dateStr);
                                }, 500);
                              }, 500);
                            }, 500);
                          }
                          
                          // Clear search since we're navigating
                          setSearchTerm("");
                        } else {
                          toast.error(data.message || "Tracking number not found");
                        }
                      } catch (error) {
                        console.error("Tracking search error:", error);
                        toast.error("Failed to search tracking");
                      }
                    }
                  }
                }}
                className="pl-10"
              />
              {searchTerm.length >= 5 && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">
                  Press Enter to search tracking
                </span>
              )}
            </div>
            
            {/* Filter Toggle Button */}
            <Button
              variant={showFilters ? "default" : "outline"}
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className={`gap-2 ${hasActiveFilters ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : ''}`}
            >
              <SlidersHorizontal className="w-4 h-4" />
              Filters
              {hasActiveFilters && (
                <span className="ml-1 px-1.5 py-0.5 text-xs rounded-full bg-indigo-600 text-white">
                  {Object.values(filters).filter(v => v !== "all" && v !== "").length
                    + (saleTeamFilter !== "all" ? 1 : 0)
                    + (underQuickFilter !== "all" ? 1 : 0)
                    + (autoUnclassifiedFilter ? 1 : 0)}
                </span>
              )}
            </Button>
            
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={resetFilters} className="gap-2 text-red-600 hover:bg-red-50">
                <XCircle className="w-4 h-4" />
                Clear Filters
              </Button>
            )}
          </div>
          </div>
          
          {/* Warning Banner for Unassigned Orders */}
          {ordersWithoutClient > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <Package className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="font-medium text-amber-800">
                    {ordersWithoutClient} orders without client code
                  </p>
                  <p className="text-sm text-amber-600">
                    These orders appear in Shipment Costing ({totalParcels} total) but not in Ledger ({totalParcels - ordersWithoutClient} assigned)
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowUnassignedDialog(true);
                  fetchUnassignedOrders(1, "");
                }}
                className="border-amber-300 text-amber-700 hover:bg-amber-100"
              >
                <Users className="w-4 h-4 mr-2" />
                Assign Client Codes
              </Button>
            </div>
          )}
          
          {/* Filters Panel */}
          {showFilters && (
            <div className="bg-slate-50 border rounded-lg p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-sm flex items-center gap-2">
                  <Filter className="w-4 h-4" />
                  Filter Options
                </h3>
                <Button variant="ghost" size="sm" onClick={() => setShowFilters(false)} className="h-6 w-6 p-0">
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {/* Ledger Type (Clients/Partners) */}
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">Ledger Type</Label>
                  <Select value={filters.ledgerType} onValueChange={(v) => setFilters(f => ({ ...f, ledgerType: v }))}>
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="All Types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="Debtors/Customers">Debtors/Customers</SelectItem>
                      <SelectItem value="Supplier/Creditor">Supplier/Creditor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Role */}
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">Role</Label>
                  <Select value={filters.role} onValueChange={(v) => setFilters(f => ({ ...f, role: v }))}>
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="All Roles" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Roles</SelectItem>
                      {uniqueRoles.map(role => (
                        <SelectItem key={role} value={role}>{role === "Client" ? "DP CLIENT" : role}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Under (Handled By) */}
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-purple-700">Under (Handled By)</Label>
                  <Select value={filters.under} onValueChange={(v) => setFilters(f => ({ ...f, under: v }))}>
                    <SelectTrigger className="h-8 text-xs border-purple-200">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Handlers</SelectItem>
                      {uniqueUnder.map(under => (
                        <SelectItem key={under} value={under}>{under}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Balance Status */}
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">Balance Status</Label>
                  <Select value={filters.balanceStatus} onValueChange={(v) => setFilters(f => ({ ...f, balanceStatus: v }))}>
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Balances</SelectItem>
                      <SelectItem value="debit_only">Debit Only (No Credit)</SelectItem>
                      <SelectItem value="credit_only">Credit Only (No Debit)</SelectItem>
                      <SelectItem value="positive">Positive Balance (Owes Us)</SelectItem>
                      <SelectItem value="negative">Negative Balance (We Owe)</SelectItem>
                      <SelectItem value="zero_balance">Zero Balance</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Agreement */}
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">Agreement</Label>
                  <Select value={filters.agreement} onValueChange={(v) => setFilters(f => ({ ...f, agreement: v }))}>
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="Yes">With Agreement</SelectItem>
                      <SelectItem value="No">Without Agreement</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Has Orders */}
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">Orders</Label>
                  <Select value={filters.hasOrders} onValueChange={(v) => setFilters(f => ({ ...f, hasOrders: v }))}>
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="with_orders">With Orders</SelectItem>
                      <SelectItem value="without_orders">Without Orders</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Amount Range Filters */}
              <div className="border-t pt-4">
                <Label className="text-xs font-medium mb-2 block">Amount Ranges (₹)</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-red-600">Min Debit</Label>
                    <Input
                      type="number"
                      placeholder="0"
                      value={filters.minDebit}
                      onChange={(e) => setFilters(f => ({ ...f, minDebit: e.target.value }))}
                      className="h-8 text-xs"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-red-600">Max Debit</Label>
                    <Input
                      type="number"
                      placeholder="∞"
                      value={filters.maxDebit}
                      onChange={(e) => setFilters(f => ({ ...f, maxDebit: e.target.value }))}
                      className="h-8 text-xs"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-green-600">Min Credit</Label>
                    <Input
                      type="number"
                      placeholder="0"
                      value={filters.minCredit}
                      onChange={(e) => setFilters(f => ({ ...f, minCredit: e.target.value }))}
                      className="h-8 text-xs"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-green-600">Max Credit</Label>
                    <Input
                      type="number"
                      placeholder="∞"
                      value={filters.maxCredit}
                      onChange={(e) => setFilters(f => ({ ...f, maxCredit: e.target.value }))}
                      className="h-8 text-xs"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-indigo-600">Min Balance</Label>
                    <Input
                      type="number"
                      placeholder="-∞"
                      value={filters.minBalance}
                      onChange={(e) => setFilters(f => ({ ...f, minBalance: e.target.value }))}
                      className="h-8 text-xs"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-indigo-600">Max Balance</Label>
                    <Input
                      type="number"
                      placeholder="∞"
                      value={filters.maxBalance}
                      onChange={(e) => setFilters(f => ({ ...f, maxBalance: e.target.value }))}
                      className="h-8 text-xs"
                    />
                  </div>
                </div>
              </div>
              
              {/* Date Range Filters */}
              <div className="border-t pt-4">
                <Label className="text-xs font-medium mb-2 block flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-blue-500" />
                  Date Range Filter (Join Date)
                </Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-blue-600">From Date</Label>
                    <DMYDateInput
  value={filters.dateFrom}
                      onChange={(e) => setFilters(f => ({ ...f, dateFrom: e.target.value }))}
                      className="h-8 text-xs"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-blue-600">To Date</Label>
                    <DMYDateInput
  value={filters.dateTo}
                      onChange={(e) => setFilters(f => ({ ...f, dateTo: e.target.value }))}
                      className="h-8 text-xs"
                    />
                  </div>
                  <div className="flex items-end gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 text-xs"
                      onClick={() => {
                        const today = new Date();
                        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
                        setFilters(f => ({
                          ...f,
                          dateFrom: firstDay.toISOString().split('T')[0],
                          dateTo: today.toISOString().split('T')[0]
                        }));
                      }}
                    >
                      This Month
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 text-xs"
                      onClick={() => {
                        const today = new Date();
                        const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                        const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
                        setFilters(f => ({
                          ...f,
                          dateFrom: lastMonth.toISOString().split('T')[0],
                          dateTo: lastMonthEnd.toISOString().split('T')[0]
                        }));
                      }}
                    >
                      Last Month
                    </Button>
                  </div>
                  <div className="flex items-end gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 text-xs"
                      onClick={() => {
                        const today = new Date();
                        const firstDay = new Date(today.getFullYear(), 0, 1);
                        setFilters(f => ({
                          ...f,
                          dateFrom: firstDay.toISOString().split('T')[0],
                          dateTo: today.toISOString().split('T')[0]
                        }));
                      }}
                    >
                      This Year
                    </Button>
                    {(filters.dateFrom || filters.dateTo) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 text-xs text-red-600"
                        onClick={() => setFilters(f => ({ ...f, dateFrom: "", dateTo: "" }))}
                      >
                        Clear Dates
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Quick Filters */}
              <div className="border-t pt-4 flex flex-wrap gap-2">
                <Label className="text-xs font-medium mr-2 self-center">Quick:</Label>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => setFilters(f => ({ ...f, balanceStatus: "positive" }))}
                >
                  💰 Owes Us Money
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => setFilters(f => ({ ...f, balanceStatus: "debit_only" }))}
                >
                  📊 Debit Only
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => setFilters(f => ({ ...f, balanceStatus: "credit_only" }))}
                >
                  💳 Credit Only
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => setFilters(f => ({ ...f, hasOrders: "with_orders" }))}
                >
                  📦 With Orders
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => setFilters(f => ({ ...f, agreement: "Yes" }))}
                >
                  ✅ With Agreement
                </Button>
              </div>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredLedgers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 border rounded-lg bg-white">
              <BookOpen className="w-16 h-16 text-slate-300" />
              <p className="text-slate-500 font-medium mt-4">
                {hasActiveFilters ? "No ledgers match your filters" : "No ledgers found"}
              </p>
              {hasActiveFilters ? (
                <Button variant="outline" size="sm" onClick={resetFilters} className="mt-4 gap-2">
                  <XCircle className="w-4 h-4" />
                  Clear Filters
                </Button>
              ) : (
                <Button variant="outline" size="sm" onClick={autoGenerateLedgers} className="mt-4 gap-2">
                  <Zap className="w-4 h-4" />
                  Auto Generate from Orders
              </Button>
            )}
            </div>
          ) : viewMode === "table" ? (
            /* TABLE VIEW */
            <div className="bg-white rounded-xl border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100 sticky top-0">
                    <tr>
                      <th className="px-2 py-3 text-center font-semibold text-slate-500 w-12">#</th>
                      <th 
                        className="px-3 py-3 text-left font-semibold text-slate-700 cursor-pointer hover:bg-slate-200 transition-colors select-none"
                        onClick={() => handleSort('client_code')}
                      >
                        <div className="flex items-center">
                          Account {getSortIcon('client_code')}
                        </div>
                      </th>
                      <th 
                        className="px-3 py-3 text-left font-semibold text-slate-700 cursor-pointer hover:bg-slate-200 transition-colors select-none"
                        onClick={() => handleSort('name')}
                      >
                        <div className="flex items-center">
                          Name {getSortIcon('name')}
                        </div>
                      </th>
                      <th 
                        className="px-3 py-3 text-center font-semibold text-slate-700 cursor-pointer hover:bg-slate-200 transition-colors select-none"
                        onClick={() => handleSort('role')}
                      >
                        <div className="flex items-center justify-center">
                          Role {getSortIcon('role')}
                        </div>
                      </th>
                      <th 
                        className="px-3 py-3 text-left font-semibold text-purple-700 bg-purple-50 cursor-pointer hover:bg-purple-100 transition-colors select-none"
                        onClick={() => handleSort('under')}
                      >
                        <div className="flex items-center">
                          Under {getSortIcon('under')}
                        </div>
                      </th>
                      {showHiddenColumns && (
                        <>
                          <th className="px-3 py-3 text-center font-semibold text-teal-700 bg-teal-50 whitespace-nowrap" title="Profit share: Self / Share 2 / Share 3 (Partner) — double-click a cell to edit">
                            %
                          </th>
                          <th 
                            className="px-3 py-3 text-left font-semibold text-slate-700 cursor-pointer hover:bg-slate-200 transition-colors select-none"
                            onClick={() => handleSort('phone_number')}
                          >
                            <div className="flex items-center">
                              Phone {getSortIcon('phone_number')}
                            </div>
                          </th>
                          <th className="px-3 py-3 text-left font-semibold text-slate-700">GST</th>
                          <th className="px-3 py-3 text-left font-semibold text-slate-700">IEC</th>
                          <th 
                            className="px-3 py-3 text-left font-semibold text-slate-700 cursor-pointer hover:bg-slate-200 transition-colors select-none"
                            onClick={() => handleSort('website_name')}
                          >
                            <div className="flex items-center">
                              Website {getSortIcon('website_name')}
                            </div>
                          </th>
                          <th 
                            className="px-3 py-3 text-center font-semibold text-slate-700 cursor-pointer hover:bg-slate-200 transition-colors select-none"
                            onClick={() => handleSort('agreement')}
                          >
                            <div className="flex items-center justify-center">
                              Agreement {getSortIcon('agreement')}
                            </div>
                          </th>
                          <th className="px-3 py-3 text-left font-semibold text-slate-700">Buy Leads</th>
                          <th 
                            className="px-3 py-3 text-left font-semibold text-slate-700 cursor-pointer hover:bg-slate-200 transition-colors select-none"
                            onClick={() => handleSort('join_date')}
                          >
                            <div className="flex items-center">
                              Join Date {getSortIcon('join_date')}
                            </div>
                          </th>
                        </>
                      )}
                      <th 
                        className="px-3 py-3 text-right font-semibold text-slate-700 cursor-pointer hover:bg-slate-200 transition-colors select-none"
                        onClick={() => handleSort('order_count')}
                      >
                        <div className="flex items-center justify-end">
                          Orders {getSortIcon('order_count')}
                        </div>
                      </th>
                      <th 
                        className="px-3 py-3 text-right font-semibold text-red-700 bg-red-50 cursor-pointer hover:bg-red-100 transition-colors select-none"
                        onClick={() => handleSort('debit')}
                      >
                        <div className="flex items-center justify-end">
                          Debit (₹) {getSortIcon('debit')}
                        </div>
                      </th>
                      <th 
                        className="px-3 py-3 text-right font-semibold text-green-700 bg-green-50 cursor-pointer hover:bg-green-100 transition-colors select-none"
                        onClick={() => handleSort('credit')}
                      >
                        <div className="flex items-center justify-end">
                          Credit (₹) {getSortIcon('credit')}
                        </div>
                      </th>
                      <th 
                        className="px-3 py-3 text-right font-semibold text-indigo-700 bg-indigo-50 cursor-pointer hover:bg-indigo-100 transition-colors select-none"
                        onClick={() => handleSort('balance')}
                      >
                        <div className="flex items-center justify-end">
                          Balance (₹) {getSortIcon('balance')}
                        </div>
                      </th>
                      <th className="px-3 py-3 text-center font-semibold text-slate-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLedgers.map((ledger, idx) => (
                      <tr 
                        key={ledger.client_code}
                        className={`border-b hover:bg-blue-50 cursor-pointer transition-colors ${idx % 2 === 0 ? "bg-white" : "bg-slate-50"}`}
                        onClick={() => selectLedger(ledger.client_code)}
                        onDoubleClick={(e) => { e.stopPropagation(); handleDoubleClick(ledger); }}
                        title="Click to view details, Double-click to add payment"
                      >
                        <td className="px-2 py-2 text-center text-slate-400 text-xs font-medium">{idx + 1}</td>
                        <td className="px-3 py-2 font-semibold text-indigo-600">
                          <div className="flex items-center gap-1.5">
                            <span>{ledger.client_code}</span>
                            {(() => {
                              const h = healthBatch[ledger.client_code?.toUpperCase()] || {};
                              const totalReships = (h.reship_free || 0) + (h.reship_paid || 0);
                              if (totalReships === 0) return null;
                              const tone = totalReships >= 5
                                ? "bg-red-100 text-red-700 border-red-200"
                                : "bg-purple-100 text-purple-700 border-purple-200";
                              return (
                                <span
                                  className={`inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full border ${tone}`}
                                  title={`${h.reship_free || 0} free · ${h.reship_paid || 0} paid reship — click the Activity icon to view breakdown`}
                                  data-testid={`ledger-health-pill-${ledger.client_code}`}
                                >
                                  {totalReships}↻
                                </span>
                              );
                            })()}
                          </div>
                        </td>
                        <td className="px-3 py-2">{ledger.name || "-"}</td>
                        <td className="px-3 py-2 text-center">
                          {ledger.role === "Partner" && (
                            <span className="px-2 py-0.5 text-xs font-medium rounded bg-purple-100 text-purple-700">Partner</span>
                          )}
                          {ledger.role === "Agent" && (
                            <span className="px-2 py-0.5 text-xs font-medium rounded bg-blue-100 text-blue-700">Agent</span>
                          )}
                          {ledger.role === "Client" && (
                            <span className="px-2 py-0.5 text-xs font-medium rounded bg-green-100 text-green-700">DP CLIENT</span>
                          )}
                          {ledger.role === "Supplier" && (
                            <span className="px-2 py-0.5 text-xs font-medium rounded bg-orange-100 text-orange-700">Supplier</span>
                          )}
                          {!ledger.role && (
                            <span className="text-xs text-slate-400">-</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-purple-600 font-medium bg-purple-50/30">{ledger.under || "-"}</td>
                        {showHiddenColumns && (
                          <>
                            <td
                              className="px-3 py-2 text-center bg-teal-50/30 cursor-pointer hover:bg-teal-100/50"
                              onClick={(e) => e.stopPropagation()}
                              onDoubleClick={(e) => {
                                e.stopPropagation();
                                setRatioEdit({
                                  ledger,
                                  r1: ledger.profit_ratio_1 || "",
                                  r2: ledger.profit_ratio_2 || "",
                                  r3: ledger.profit_ratio_3 || "",
                                });
                              }}
                              data-testid={`ledger-ratio-${ledger.client_code}`}
                            >
                              {ratioEdit?.ledger?.client_code === ledger.client_code ? (
                                <div className="flex items-center gap-0.5 justify-center" onClick={(e) => e.stopPropagation()}>
                                  {["r1", "r2", "r3"].map((k, i) => (
                                    <input
                                      key={k}
                                      autoFocus={i === 0}
                                      value={ratioEdit[k]}
                                      onChange={(e) => setRatioEdit((p) => ({ ...p, [k]: e.target.value }))}
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter") saveRatioEdit();
                                        if (e.key === "Escape") setRatioEdit(null);
                                      }}
                                      placeholder={["Self", "Sh2", "Sh3"][i]}
                                      className="w-11 border border-teal-400 rounded px-1 py-0.5 text-[11px] text-center focus:outline-none"
                                      data-testid={`ratio-input-${k}`}
                                    />
                                  ))}
                                  <button
                                    onClick={saveRatioEdit}
                                    disabled={ratioEdit.saving}
                                    className="text-emerald-600 hover:text-emerald-800 px-0.5 font-bold text-sm"
                                    title="Save (Enter)"
                                    data-testid="ratio-save-btn"
                                  >
                                    ✓
                                  </button>
                                </div>
                              ) : (() => {
                                const slot = (v) => {
                                  const n = parseFloat(v);
                                  return v !== "" && v != null && !isNaN(n) ? String(n).replace(/\.0+$/, "") : "—";
                                };
                                const s1 = slot(ledger.profit_ratio_1);
                                const s2 = slot(ledger.profit_ratio_2);
                                const s3 = slot(ledger.profit_ratio_3);
                                const empty = s1 === "—" && s2 === "—" && s3 === "—";
                                return (
                                  <span
                                    className={`text-xs font-mono font-semibold whitespace-nowrap ${empty ? "text-slate-300" : "text-teal-700"}`}
                                    title={`Double-click to edit — ${empty ? "No profit ratios set" : `Self ${s1}% / ${ledger.profit_share_2_name || "Share 2"} ${s2}% / ${ledger.profit_share_3_name || "Share 3"} ${s3}%`}`}
                                  >
                                    {s1}/{s2}/{s3}
                                  </span>
                                );
                              })()}
                            </td>
                            <td className="px-3 py-2">{ledger.phone_number || "-"}</td>
                            <td className="px-3 py-2 font-mono text-xs">{ledger.gst_number || "-"}</td>
                            <td className="px-3 py-2 font-mono text-xs">{ledger.iec_code || "-"}</td>
                            <td className="px-3 py-2">{ledger.website_name || "-"}</td>
                            <td className="px-3 py-2 text-center">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${ledger.agreement === "Yes" ? "bg-green-100 text-green-700" : "bg-orange-100 text-orange-700"}`}>
                                {ledger.agreement || "No"}
                              </span>
                            </td>
                            <td className="px-3 py-2">{ledger.buy_leads || "-"}</td>
                            <td className="px-3 py-2">{ledger.join_date || "-"}</td>
                          </>
                        )}
                        <td className="px-3 py-2 text-right">{ledger.order_count}</td>
                        <td className="px-3 py-2 text-right text-red-600 font-medium bg-red-50/50">{formatCurrency(ledger.debit)}</td>
                        <td className="px-3 py-2 text-right text-green-600 font-medium bg-green-50/50">{formatCurrency(ledger.credit)}</td>
                        <td className={`px-3 py-2 text-right font-bold bg-indigo-50/50 ${ledger.balance > 0 ? "text-red-600" : ledger.balance < 0 ? "text-green-600" : "text-slate-600"}`}>
                          {formatCurrency(ledger.balance)}
                        </td>
                        <td className="px-3 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                          <div className="flex gap-1 justify-center items-center">
                            <ClientHealthPopover clientCode={ledger.client_code} token={token} />
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); setEditingLedger({ ...ledger }); setShowEditDialog(true); }}
                              className="h-7 w-7 p-0"
                            >
                              <Edit3 className="w-3 h-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); fetchPayments(ledger.client_code); }}
                              className="h-7 w-7 p-0 text-green-600"
                            >
                              <CreditCard className="w-3 h-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); deleteLedger(ledger.client_code); }}
                              className="h-7 w-7 p-0 text-red-600"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => { 
                                e.stopPropagation(); 
                                setPriceListClient({ code: ledger.client_code, name: ledger.name });
                                setShowPriceListDialog(true);
                              }}
                              className="h-7 w-7 p-0 text-indigo-600"
                              title="Price List"
                            >
                              <List className="w-3 h-3" />
                            </Button>
                            {ledger.role === "Client" && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => { 
                                  e.stopPropagation(); 
                                  setShippingRatesClient({ code: ledger.client_code, name: ledger.name });
                                  setShowShippingRatesDialog(true);
                                }}
                                className="h-7 w-7 p-0 text-blue-600"
                                title="Shipping Rates"
                              >
                                <Truck className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-slate-200 font-bold">
                    <tr>
                      <td colSpan={showHiddenColumns ? 12 : 4} className="px-3 py-3 text-right">
                        {hasActiveFilters ? `Filtered Total (${filteredLedgers.length} rows):` : 'Grand Total:'}
                      </td>
                      <td className="px-3 py-3 text-right text-slate-700">{filteredTotals.total_orders}</td>
                      <td className="px-3 py-3 text-right text-red-700">{formatCurrency(filteredTotals.total_debit)}</td>
                      <td className="px-3 py-3 text-right text-green-700">{formatCurrency(filteredTotals.total_credit)}</td>
                      <td className="px-3 py-3 text-right text-indigo-700">{formatCurrency(filteredTotals.total_balance)}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          ) : (
            /* CARDS VIEW */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredLedgers.map((ledger) => (
                <div
                  key={ledger.client_code}
                  className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-lg transition-shadow cursor-pointer group"
                  onClick={() => selectLedger(ledger.client_code)}
                  onDoubleClick={(e) => { e.stopPropagation(); handleDoubleClick(ledger); }}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-bold text-lg text-slate-800">{ledger.client_code}</h3>
                      {ledger.name && <p className="text-sm text-slate-600 mt-1">{ledger.name}</p>}
                      <div className="flex flex-wrap gap-2 mt-2">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${ledger.ledger_type === "Supplier/Creditor" ? "bg-orange-100 text-orange-700" : "bg-blue-100 text-blue-700"}`}>
                          {ledger.ledger_type || "Debtors/Customers"}
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-indigo-500 transition-colors" />
                  </div>
                  
                  <div className="mt-3 pt-3 border-t grid grid-cols-3 gap-2 text-xs">
                    <div className="text-center">
                      <p className="text-slate-500">Debit</p>
                      <p className="font-semibold text-red-600">{formatCurrency(ledger.debit)}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-slate-500">Credit</p>
                      <p className="font-semibold text-green-600">{formatCurrency(ledger.credit)}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-slate-500">Balance</p>
                      <p className={`font-bold ${ledger.balance > 0 ? "text-red-600" : "text-green-600"}`}>{formatCurrency(ledger.balance)}</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-2 mt-3 pt-3 border-t" onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="sm" onClick={() => { setEditingLedger({ ...ledger }); setShowEditDialog(true); }} className="flex-1 h-8 text-xs">
                      <Edit3 className="w-3 h-3 mr-1" /> Edit
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => deleteLedger(ledger.client_code)} className="flex-1 h-8 text-xs text-red-600 hover:text-red-700 hover:bg-red-50">
                      <Trash2 className="w-3 h-3 mr-1" /> Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        /* DETAIL VIEW WITH DRILL-DOWN */
        <div className="space-y-4">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl border p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Package className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total Orders</p>
                  <p className="text-xl font-bold text-slate-800">{summaryData.reduce((a, b) => a + b.order_count, 0)}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total Debit</p>
                  <p className="text-lg font-bold text-red-600">{formatCurrency(grandTotal.total_debit)}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total Credit</p>
                  <p className="text-lg font-bold text-green-600">{formatCurrency(grandTotal.total_credit)}</p>
                </div>
              </div>
            </div>
            {grandTotal.total_collection > 0 && (
              <div className="bg-white rounded-xl border p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                    <IndianRupee className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Collection (Self)</p>
                    <p className="text-lg font-bold text-amber-600">{formatCurrency(grandTotal.total_collection)}</p>
                  </div>
                </div>
              </div>
            )}
            <div className="bg-white rounded-xl border p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                  <IndianRupee className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Balance</p>
                  <p className={`text-lg font-bold ${grandTotal.total_balance > 0 ? "text-red-600" : "text-green-600"}`}>{formatCurrency(grandTotal.total_balance)}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Add Payment Button */}
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setDebitData({ client_code: selectedLedger, amount: "", debit_date: new Date().toISOString().split("T")[0], debit_type: "", reference_no: "", remarks: "" });
                setShowDebitDialog(true);
              }}
              className="gap-2 border-red-300 hover:bg-red-50"
              data-testid="add-debit-btn"
            >
              <IndianRupee className="w-4 h-4 text-red-500" />
              Add Debit
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setPaymentData({ client_code: selectedLedger, amount: "", payment_date: new Date().toISOString().split("T")[0], payment_mode: "", reference_no: "", remarks: "" });
                setShowPaymentDialog(true);
              }}
              className="gap-2 border-green-300 hover:bg-green-50"
              data-testid="add-payment-btn"
            >
              <CreditCard className="w-4 h-4 text-green-500" />
              Add Credit
            </Button>
          </div>

          {/* Day Details View */}
          {dayDetails ? (
            <div className="space-y-4">
              {/* Highlighted tracking banner */}
              {highlightedTracking && (
                <div className="bg-yellow-100 border-2 border-yellow-400 rounded-xl p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Search className="w-5 h-5 text-yellow-600" />
                    <span className="font-semibold text-yellow-800">
                      Searched Tracking: <span className="font-mono bg-yellow-200 px-2 py-1 rounded">{highlightedTracking}</span>
                    </span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setHighlightedTracking(null)}
                    className="border-yellow-400 text-yellow-700 hover:bg-yellow-200"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Clear Highlight
                  </Button>
                </div>
              )}
              
              <div className="bg-white rounded-xl border p-4">
                <h3 className="font-semibold text-slate-800 mb-4">
                  Orders for {formatPeriod(dayDetails.date, "day")} ({dayDetails.orders.length} items)
                </h3>
                <div className="overflow-x-auto max-h-96">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left">Tracking</th>
                        <th className="px-3 py-2 text-left">Product</th>
                        <th className="px-3 py-2 text-right">Qty</th>
                        <th className="px-3 py-2 text-right">Med. Cost</th>
                        <th className="px-3 py-2 text-right">Ship. Cost</th>
                        <th className="px-3 py-2 text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dayDetails.orders.map((order, idx) => {
                        const isHighlighted = highlightedTracking && order.tracking === highlightedTracking;
                        const isReplacedTracking = order.is_strikethrough === true;
                        const replacementNote = order.replacement_note || (order.superseded_by ? `Replaced by ${order.superseded_by}` : '');
                        return (
                          <tr 
                            key={idx} 
                            className={`border-b hover:bg-slate-50 ${
                              isHighlighted ? 'bg-yellow-100 ring-2 ring-yellow-400 animate-pulse' : 
                              isReplacedTracking ? 'bg-violet-50/80 opacity-70' : ''
                            }`}
                            id={isHighlighted ? 'highlighted-tracking' : undefined}
                            style={isReplacedTracking ? { textDecoration: 'line-through', textDecorationColor: '#8b5cf6' } : {}}
                          >
                            <td className={`px-3 py-2 font-mono text-xs ${isHighlighted ? 'font-bold text-yellow-800' : ''}`}>
                              <div className="flex flex-col gap-0.5">
                                <span>{order.tracking}</span>
                                {isHighlighted && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-500 text-white w-fit">
                                    ← SEARCHED
                                  </span>
                                )}
                                {isReplacedTracking && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-violet-500 text-white w-fit" title={replacementNote}>
                                    REPLACED {order.superseded_by ? `→ ${order.superseded_by}` : ''}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className={`px-3 py-2 ${isHighlighted ? 'font-semibold' : ''}`}>{order.second_name || order.product}</td>
                            <td className={`px-3 py-2 text-right ${isHighlighted ? 'font-semibold' : ''}`}>{order.qty}</td>
                            <td className={`px-3 py-2 text-right text-green-600 ${isHighlighted ? 'font-bold' : ''}`}>{formatCurrency(order.medicine_cost)}</td>
                            <td className={`px-3 py-2 text-right text-orange-600 ${isHighlighted ? 'font-bold' : ''}`}>{order.is_order_first_row ? formatCurrency(order.shipping_cost) : "-"}</td>
                            <td className={`px-3 py-2 text-right font-semibold ${isHighlighted ? 'text-yellow-800' : ''}`}>
                              {order.is_order_first_row ? (
                                isReplacedTracking ? (
                                  <span className="text-violet-600" title="Cost moved to new tracking">₹0.00</span>
                                ) : formatCurrency(order.total_cost)
                              ) : "-"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot className="bg-slate-200 font-bold">
                      <tr>
                        <td colSpan={5} className="px-3 py-2 text-right">Day Total (Debit):</td>
                        <td className="px-3 py-2 text-right text-red-600">{formatCurrency(dayDetails.totals.debit)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              {dayDetails.payments.length > 0 && (
                <div className="bg-white rounded-xl border p-4">
                  <h3 className="font-semibold text-slate-800 mb-4">
                    Payments on {formatPeriod(dayDetails.date, "day")} ({dayDetails.payments.length} entries)
                  </h3>
                  <table className="w-full text-sm">
                    <thead className="bg-green-50">
                      <tr>
                        <th className="px-3 py-2 text-left">Mode</th>
                        <th className="px-3 py-2 text-left">Reference</th>
                        <th className="px-3 py-2 text-left">Remarks</th>
                        <th className="px-3 py-2 text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dayDetails.payments.map((payment, idx) => (
                        <tr key={idx} className="border-b">
                          <td className="px-3 py-2">{payment.payment_mode || "-"}</td>
                          <td className="px-3 py-2">{payment.reference_no || "-"}</td>
                          <td className="px-3 py-2">{payment.remarks || "-"}</td>
                          <td className="px-3 py-2 text-right text-green-600 font-semibold">{formatCurrency(payment.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-green-100 font-bold">
                      <tr>
                        <td colSpan={3} className="px-3 py-2 text-right">Day Total (Credit):</td>
                        <td className="px-3 py-2 text-right text-green-600">{formatCurrency(dayDetails.totals.credit)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}

              <div className="bg-indigo-50 rounded-xl border border-indigo-200 p-4">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-sm text-slate-500">Day Debit</p>
                    <p className="text-xl font-bold text-red-600">{formatCurrency(dayDetails.totals.debit)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Day Credit</p>
                    <p className="text-xl font-bold text-green-600">{formatCurrency(dayDetails.totals.credit)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Day Balance</p>
                    <p className={`text-xl font-bold ${dayDetails.totals.balance > 0 ? "text-red-600" : "text-green-600"}`}>{formatCurrency(dayDetails.totals.balance)}</p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Summary Table */
            <div className="bg-white rounded-xl border overflow-hidden">
              <div className="px-4 py-3 border-b bg-slate-50 flex items-center justify-between">
                <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {summaryView === "year" ? "Year-wise Summary" : 
                   summaryView === "month" ? `Monthly Summary (${selectedYear})` :
                   `Daily Summary (${selectedYear}-${String(selectedMonth).padStart(2, '0')})`}
                </h3>
                <p className="text-xs text-slate-500">Click row to drill down</p>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Period</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600">Orders</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-red-600">Debit (₹)</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-green-600">Credit (₹)</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-amber-600">Coll. (₹)</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-indigo-600">Balance (₹)</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600">Running Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summaryData.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-8 text-center text-slate-500">No data available</td>
                      </tr>
                    ) : (
                      summaryData.map((row) => (
                        <tr
                          key={row.period}
                          className="hover:bg-blue-50 cursor-pointer transition-colors border-b"
                          onClick={() => handlePeriodClick(row.period)}
                        >
                          <td className="px-4 py-3 font-medium text-blue-600">
                            {formatPeriod(row.period, summaryView)}
                            <ChevronRight className="w-4 h-4 inline ml-2 text-slate-400" />
                          </td>
                          <td className="px-4 py-3 text-right">{row.order_count}</td>
                          <td className="px-4 py-3 text-right text-red-600">{formatCurrency(row.debit)}</td>
                          <td className="px-4 py-3 text-right text-green-600">{formatCurrency(row.credit)}</td>
                          <td className="px-4 py-3 text-right text-amber-600">{row.collection ? formatCurrency(row.collection) : "-"}</td>
                          <td className={`px-4 py-3 text-right font-semibold ${row.balance > 0 ? "text-red-600" : "text-green-600"}`}>{formatCurrency(row.balance)}</td>
                          <td className={`px-4 py-3 text-right font-bold ${row.running_balance > 0 ? "text-red-700 bg-red-50" : "text-green-700 bg-green-50"}`}>{formatCurrency(row.running_balance)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                  {summaryData.length > 0 && (
                    <tfoot className="bg-slate-200 font-bold">
                      <tr>
                        <td className="px-4 py-3">Grand Total</td>
                        <td className="px-4 py-3 text-right">{summaryData.reduce((a, b) => a + b.order_count, 0)}</td>
                        <td className="px-4 py-3 text-right text-red-700">{formatCurrency(grandTotal.total_debit)}</td>
                        <td className="px-4 py-3 text-right text-green-700">{formatCurrency(grandTotal.total_credit)}</td>
                        <td className="px-4 py-3 text-right text-amber-700">{grandTotal.total_collection ? formatCurrency(grandTotal.total_collection) : "-"}</td>
                        <td className={`px-4 py-3 text-right ${grandTotal.total_balance > 0 ? "text-red-700" : "text-green-700"}`}>{formatCurrency(grandTotal.total_balance)}</td>
                        <td className="px-4 py-3"></td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Edit/Add Ledger Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-indigo-500" />
              {editingLedger?.id ? "Edit Ledger" : "Add New Ledger"}
            </DialogTitle>
          </DialogHeader>
          
          {editingLedger && (
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 border-b pb-2 mb-2">
                <h4 className="text-sm font-semibold text-slate-700">Basic Information</h4>
              </div>
              
              <div className="space-y-1.5">
                <Label>Client Code *</Label>
                <Input
                  value={editingLedger.client_code || ""}
                  onChange={(e) => setEditingLedger(prev => ({ ...prev, client_code: e.target.value.toUpperCase() }))}
                  placeholder="e.g., MAYU"
                  disabled={!!editingLedger.id}
                />
              </div>
              
              <div className="space-y-1.5">
                <Label>Name</Label>
                <Input
                  value={editingLedger.name || ""}
                  onChange={(e) => setEditingLedger(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Full name"
                />
              </div>
              
              <div className="space-y-1.5">
                <Label>Ledger Type</Label>
                <Select value={editingLedger.ledger_type || "Debtors/Customers"} onValueChange={(value) => setEditingLedger(prev => ({ ...prev, ledger_type: value }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Debtors/Customers">Debtors/Customers</SelectItem>
                    <SelectItem value="Supplier/Creditor">Supplier/Creditor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-1.5">
                <Label>Role</Label>
                <Select value={editingLedger.role || "Client"} onValueChange={(value) => setEditingLedger(prev => ({ ...prev, role: value }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Client">DP CLIENT</SelectItem>
                    <SelectItem value="Partner">Partner</SelectItem>
                    <SelectItem value="Supplier">Supplier</SelectItem>
                    <SelectItem value="Agent">Agent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-1.5">
                <Label>Under (Handled By)</Label>
                <div className="relative">
                  <Input 
                    value={editingLedger.under || ""} 
                    onChange={(e) => setEditingLedger(prev => ({ ...prev, under: e.target.value }))} 
                    placeholder="Partner code or name handling this customer"
                    list="partner-suggestions"
                  />
                  <datalist id="partner-suggestions">
                    {ledgers.filter(l => l.role === "Partner" || l.ledger_type === "Supplier/Creditor").map(l => (
                      <option key={l.client_code} value={l.client_code}>{l.name ? `${l.client_code} - ${l.name}` : l.client_code}</option>
                    ))}
                  </datalist>
                </div>
                <p className="text-xs text-slate-500">Select existing partner or enter new name</p>
              </div>

              <div className="col-span-2 border-b pb-2 mb-2 mt-4">
                <h4 className="text-sm font-semibold text-slate-700">Contact Information</h4>
              </div>
              
              <div className="space-y-1.5">
                <Label>Phone Number</Label>
                <Input value={editingLedger.phone_number || ""} onChange={(e) => setEditingLedger(prev => ({ ...prev, phone_number: e.target.value }))} placeholder="Phone number" />
              </div>
              
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input type="email" value={editingLedger.email || ""} onChange={(e) => setEditingLedger(prev => ({ ...prev, email: e.target.value }))} placeholder="Email address" />
              </div>
              
              <div className="col-span-2 space-y-1.5">
                <Label>Address</Label>
                <Input value={editingLedger.address || ""} onChange={(e) => setEditingLedger(prev => ({ ...prev, address: e.target.value }))} placeholder="Full address" />
              </div>

              <div className="space-y-1.5">
                <Label>Website</Label>
                <Input value={editingLedger.website_name || ""} onChange={(e) => setEditingLedger(prev => ({ ...prev, website_name: e.target.value }))} placeholder="Website URL" />
              </div>

              <div className="col-span-2 border-b pb-2 mb-2 mt-4">
                <h4 className="text-sm font-semibold text-slate-700">Business Information</h4>
              </div>
              
              <div className="space-y-1.5">
                <Label>GST Number</Label>
                <Input value={editingLedger.gst_number || ""} onChange={(e) => setEditingLedger(prev => ({ ...prev, gst_number: e.target.value.toUpperCase() }))} placeholder="GST Number" />
              </div>
              
              <div className="space-y-1.5">
                <Label>IEC Code</Label>
                <Input value={editingLedger.iec_code || ""} onChange={(e) => setEditingLedger(prev => ({ ...prev, iec_code: e.target.value.toUpperCase() }))} placeholder="Import Export Code" />
              </div>
              
              <div className="space-y-1.5">
                <Label>Agreement</Label>
                <Select value={editingLedger.agreement || "No"} onValueChange={(value) => setEditingLedger(prev => ({ ...prev, agreement: value }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Yes">Yes</SelectItem>
                    <SelectItem value="No">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-1.5">
                <Label className="flex items-center gap-1">
                  Can View Hidden Products
                  <span className="text-xs text-slate-400">(Product Price List)</span>
                </Label>
                <Select value={editingLedger.can_view_hidden_products ? "Yes" : "No"} onValueChange={(value) => setEditingLedger(prev => ({ ...prev, can_view_hidden_products: value === "Yes" }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="No">No - Hidden products not visible</SelectItem>
                    <SelectItem value="Yes">Yes - Can see all products</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-1.5">
                <Label>Buy Leads</Label>
                <Input value={editingLedger.buy_leads || ""} onChange={(e) => setEditingLedger(prev => ({ ...prev, buy_leads: e.target.value }))} placeholder="Buy leads info" />
              </div>

              <div className="space-y-1.5">
                <Label>Catalog Date From</Label>
                <DMYDateInput
  value={editingLedger.catalog_date_from || ""} onChange={(e) => setEditingLedger(prev => ({ ...prev, catalog_date_from: e.target.value }))} />
              </div>
              
              <div className="space-y-1.5">
                <Label>Catalog Date To</Label>
                <DMYDateInput
  value={editingLedger.catalog_date_to || ""} onChange={(e) => setEditingLedger(prev => ({ ...prev, catalog_date_to: e.target.value }))} />
              </div>
              
              <div className="space-y-1.5">
                <Label>Join Date</Label>
                <DMYDateInput
  value={editingLedger.join_date || ""} onChange={(e) => setEditingLedger(prev => ({ ...prev, join_date: e.target.value }))} />
              </div>

              <div className="col-span-2 space-y-1.5">
                <Label>Account Details</Label>
                <textarea value={editingLedger.account_details || ""} onChange={(e) => setEditingLedger(prev => ({ ...prev, account_details: e.target.value }))} placeholder="Bank account details, payment info, etc." className="w-full min-h-[80px] px-3 py-2 border rounded-md text-sm resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              </div>
              
              <div className="col-span-2 border-b pb-2 mb-2 mt-4">
                <h4 className="text-sm font-semibold text-slate-700">Profit Sharing</h4>
                <p className="text-xs text-slate-500 mt-1">Define profit sharing percentages and names for each party</p>
              </div>
              
              <div className="col-span-2">
                <div className="grid grid-cols-3 gap-4">
                  {/* Column 1 - Self */}
                  <div className="space-y-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <Label className="text-xs font-semibold text-blue-700">Share 1 (Self)</Label>
                    <Input 
                      placeholder="Name (default: Self)" 
                      value={editingLedger.profit_share_1_name || "Self"} 
                      onChange={(e) => setEditingLedger(prev => ({ ...prev, profit_share_1_name: e.target.value }))} 
                      className="text-xs"
                    />
                    <div className="relative">
                      <Input 
                        type="number"
                        placeholder="%" 
                        value={editingLedger.profit_ratio_1 || ""} 
                        onChange={(e) => setEditingLedger(prev => ({ ...prev, profit_ratio_1: e.target.value }))} 
                        className="text-xs pr-8"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">%</span>
                    </div>
                  </div>
                  
                  {/* Column 2 - Ledger/Client */}
                  <div className="space-y-2 p-3 bg-green-50 rounded-lg border border-green-200">
                    <Label className="text-xs font-semibold text-green-700">Share 2 (Ledger)</Label>
                    <Input 
                      placeholder={editingLedger.name || editingLedger.client_code || "Client name"} 
                      value={editingLedger.profit_share_2_name || ""} 
                      onChange={(e) => setEditingLedger(prev => ({ ...prev, profit_share_2_name: e.target.value }))} 
                      className="text-xs"
                    />
                    <div className="relative">
                      <Input 
                        type="number"
                        placeholder="%" 
                        value={editingLedger.profit_ratio_2 || ""} 
                        onChange={(e) => setEditingLedger(prev => ({ ...prev, profit_ratio_2: e.target.value }))} 
                        className="text-xs pr-8"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">%</span>
                    </div>
                  </div>
                  
                  {/* Column 3 - Partner */}
                  <div className="space-y-2 p-3 bg-purple-50 rounded-lg border border-purple-200">
                    <Label className="text-xs font-semibold text-purple-700">Share 3 (Partner)</Label>
                    <div className="relative">
                      <Input 
                        placeholder={editingLedger.under || "Partner code/name"} 
                        value={editingLedger.profit_share_3_name || ""} 
                        onChange={(e) => setEditingLedger(prev => ({ ...prev, profit_share_3_name: e.target.value }))} 
                        className="text-xs"
                        list="partner-suggestions-profit"
                      />
                      <datalist id="partner-suggestions-profit">
                        {editingLedger.under && <option value={editingLedger.under} />}
                        {ledgers.filter(l => l.role === "Partner").map(l => (
                          <option key={l.client_code} value={l.client_code} />
                        ))}
                      </datalist>
                    </div>
                    <div className="relative">
                      <Input 
                        type="number"
                        placeholder="%" 
                        value={editingLedger.profit_ratio_3 || ""} 
                        onChange={(e) => setEditingLedger(prev => ({ ...prev, profit_ratio_3: e.target.value }))} 
                        className="text-xs pr-8"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">%</span>
                    </div>
                    {editingLedger.under && !editingLedger.profit_share_3_name && (
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm" 
                        className="w-full h-6 text-xs text-purple-600"
                        onClick={() => setEditingLedger(prev => ({ ...prev, profit_share_3_name: prev.under }))}
                      >
                        Use &quot;{editingLedger.under}&quot; from Under field
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="col-span-2 space-y-1.5">
                <Label>Notes</Label>
                <textarea value={editingLedger.notes || ""} onChange={(e) => setEditingLedger(prev => ({ ...prev, notes: e.target.value }))} placeholder="Additional notes" className="w-full min-h-[60px] px-3 py-2 border rounded-md text-sm resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={saveLedger} className="bg-indigo-600 hover:bg-indigo-700 text-white">Save Ledger</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-green-500" />
              Add Payment / Credit
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Client Code *</Label>
              <Input
                value={paymentData.client_code}
                onChange={(e) => setPaymentData(prev => ({ ...prev, client_code: e.target.value.toUpperCase() }))}
                placeholder="Client code"
                disabled={!!selectedLedger}
              />
            </div>
            
            <div className="space-y-1.5">
              <Label>Amount (₹) *</Label>
              <Input
                type="number"
                value={paymentData.amount}
                onChange={(e) => setPaymentData(prev => ({ ...prev, amount: e.target.value }))}
                placeholder="Enter amount"
              />
            </div>
            
            <div className="space-y-1.5">
              <Label>Payment Date</Label>
              <DMYDateInput
  value={formatDMY(paymentData.payment_date)}
                onChange={(e) => setPaymentData(prev => ({ ...prev, payment_date: e.target.value }))}
              />
            </div>
            
            <div className="space-y-1.5">
              <Label>Payment Mode</Label>
              <Select value={paymentData.payment_mode} onValueChange={(value) => setPaymentData(prev => ({ ...prev, payment_mode: value }))}>
                <SelectTrigger><SelectValue placeholder="Select mode" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Cash">Cash</SelectItem>
                  <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                  <SelectItem value="UPI">UPI</SelectItem>
                  <SelectItem value="Cheque">Cheque</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-1.5">
              <Label>Reference No.</Label>
              <Input
                value={paymentData.reference_no}
                onChange={(e) => setPaymentData(prev => ({ ...prev, reference_no: e.target.value }))}
                placeholder="Transaction ID / Cheque No."
              />
            </div>
            
            <div className="space-y-1.5">
              <Label>Remarks</Label>
              <Input
                value={paymentData.remarks}
                onChange={(e) => setPaymentData(prev => ({ ...prev, remarks: e.target.value }))}
                placeholder="Additional notes"
              />
            </div>
          </div>
          
          <DialogFooter className="gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowPaymentDialog(false)}>Cancel</Button>
            <Button onClick={savePayment} className="bg-green-600 hover:bg-green-700 text-white">Add Credit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Debit Dialog */}
      <Dialog open={showDebitDialog} onOpenChange={setShowDebitDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <IndianRupee className="w-5 h-5 text-red-500" />
              Add Manual Debit
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Client Code *</Label>
              <Input
                value={debitData.client_code}
                onChange={(e) => setDebitData(prev => ({ ...prev, client_code: e.target.value.toUpperCase() }))}
                placeholder="Client code"
                disabled={!!selectedLedger}
              />
            </div>
            
            <div className="space-y-1.5">
              <Label>Amount (₹) *</Label>
              <Input
                type="number"
                value={debitData.amount}
                onChange={(e) => setDebitData(prev => ({ ...prev, amount: e.target.value }))}
                placeholder="Enter amount"
              />
            </div>
            
            <div className="space-y-1.5">
              <Label>Debit Date</Label>
              <DMYDateInput
  value={debitData.debit_date}
                onChange={(e) => setDebitData(prev => ({ ...prev, debit_date: e.target.value }))}
              />
            </div>
            
            <div className="space-y-1.5">
              <Label>Debit Type</Label>
              <Select value={debitData.debit_type} onValueChange={(value) => setDebitData(prev => ({ ...prev, debit_type: value }))}>
                <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Manual">Manual Debit</SelectItem>
                  <SelectItem value="Adjustment">Adjustment</SelectItem>
                  <SelectItem value="Expense">Expense</SelectItem>
                  <SelectItem value="Penalty">Penalty</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-1.5">
              <Label>Reference No.</Label>
              <Input
                value={debitData.reference_no}
                onChange={(e) => setDebitData(prev => ({ ...prev, reference_no: e.target.value }))}
                placeholder="Invoice No. / Reference"
              />
            </div>
            
            <div className="space-y-1.5">
              <Label>Remarks</Label>
              <Input
                value={debitData.remarks}
                onChange={(e) => setDebitData(prev => ({ ...prev, remarks: e.target.value }))}
                placeholder="Additional notes"
              />
            </div>
          </div>
          
          <DialogFooter className="gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowDebitDialog(false)}>Cancel</Button>
            <Button onClick={saveDebit} className="bg-red-600 hover:bg-red-700 text-white">Add Debit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Debits List Dialog */}
      <Dialog open={showDebitsListDialog} onOpenChange={setShowDebitsListDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <IndianRupee className="w-5 h-5 text-red-500" />
              Debit History: {debitsClient}
            </DialogTitle>
          </DialogHeader>
          
          {debitsList.length === 0 ? (
            <p className="text-center text-slate-500 py-8">No manual debits recorded</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-100">
                <tr>
                  <th className="px-3 py-2 text-left">Date</th>
                  <th className="px-3 py-2 text-left">Type</th>
                  <th className="px-3 py-2 text-left">Reference</th>
                  <th className="px-3 py-2 text-left">Remarks</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                  <th className="px-3 py-2 text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {debitsList.map((debit) => (
                  <tr key={debit.id} className="border-b">
                    <td className="px-3 py-2">{debit.debit_date}</td>
                    <td className="px-3 py-2">{debit.debit_type}</td>
                    <td className="px-3 py-2">{debit.reference_no || "-"}</td>
                    <td className="px-3 py-2">{debit.remarks || "-"}</td>
                    <td className="px-3 py-2 text-right font-semibold text-red-600">₹{debit.amount?.toLocaleString()}</td>
                    <td className="px-3 py-2 text-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteDebit(debit.id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-slate-100 font-semibold">
                <tr>
                  <td colSpan={4} className="px-3 py-2 text-right">Total:</td>
                  <td className="px-3 py-2 text-right text-red-600">₹{debitsList.reduce((sum, d) => sum + (d.amount || 0), 0).toLocaleString()}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          )}
        </DialogContent>
      </Dialog>

      {/* Payments List Dialog */}
      <Dialog open={showPaymentsListDialog} onOpenChange={setShowPaymentsListDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-green-500" />
              Payment History: {paymentsClient}
            </DialogTitle>
          </DialogHeader>
          
          {paymentsList.length === 0 ? (
            <p className="text-center text-slate-500 py-8">No payments recorded</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-100">
                <tr>
                  <th className="px-3 py-2 text-left">Date</th>
                  <th className="px-3 py-2 text-left">Mode</th>
                  <th className="px-3 py-2 text-left">Reference</th>
                  <th className="px-3 py-2 text-left">Remarks</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {paymentsList.map((payment) => (
                  <tr key={payment.id} className="border-b">
                    <td className="px-3 py-2">{formatDMY(payment.payment_date)}</td>
                    <td className="px-3 py-2">{payment.payment_mode || "-"}</td>
                    <td className="px-3 py-2">{payment.reference_no || "-"}</td>
                    <td className="px-3 py-2">{payment.remarks || "-"}</td>
                    <td className="px-3 py-2 text-right text-green-600 font-semibold">{formatCurrency(payment.amount)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-green-50 font-bold">
                <tr>
                  <td colSpan={4} className="px-3 py-2 text-right">Total Credits:</td>
                  <td className="px-3 py-2 text-right text-green-600">{formatCurrency(paymentsList.reduce((a, b) => a + b.amount, 0))}</td>
                </tr>
              </tfoot>
            </table>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentsListDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DP CLIENTS Price List Dialog */}
      <Dialog open={showPriceListDialog} onOpenChange={setShowPriceListDialog}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <List className="w-5 h-5 text-indigo-600" />
              DP CLIENTS Price List
            </DialogTitle>
          </DialogHeader>
          {priceListClient && (
            <ClientPriceList 
              clientCode={priceListClient.code}
              clientName={priceListClient.name}
              onClose={() => setShowPriceListDialog(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* DP CLIENTS Shipping Rates Dialog */}
      <Dialog open={showShippingRatesDialog} onOpenChange={setShowShippingRatesDialog}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-blue-600" />
              DP CLIENT Shipping Rates
            </DialogTitle>
          </DialogHeader>
          {shippingRatesClient && (
            <ClientShippingRates 
              clientCode={shippingRatesClient.code}
              clientName={shippingRatesClient.name}
              onClose={() => setShowShippingRatesDialog(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Unassigned Orders Dialog */}
      <Dialog open={showUnassignedDialog} onOpenChange={setShowUnassignedDialog}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-amber-500" />
              Assign Client Codes to Orders
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden flex flex-col gap-4">
            {/* Stats and Search */}
            <div className="flex items-center justify-between gap-4">
              <div className="text-sm text-slate-600">
                <span className="font-semibold text-amber-600">{unassignedTotal}</span> orders without client code
              </div>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search tracking..."
                    value={unassignedSearch}
                    onChange={(e) => setUnassignedSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        fetchUnassignedOrders(1, unassignedSearch);
                      }
                    }}
                    className="pl-10 w-64"
                  />
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchUnassignedOrders(1, unassignedSearch)}
                >
                  Search
                </Button>
              </div>
            </div>
            
            {/* Assignment Controls */}
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border">
              <span className="text-sm font-medium">Assign selected to:</span>
              <Select value={assignClientCode} onValueChange={setAssignClientCode}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Select client code" />
                </SelectTrigger>
                <SelectContent>
                  {availableClientCodes.map(code => (
                    <SelectItem key={code} value={code}>{code}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                onClick={handleAssignClientCode}
                disabled={!assignClientCode || selectedUnassignedOrders.length === 0}
                className="bg-green-600 hover:bg-green-700"
              >
                Assign {selectedUnassignedOrders.length > 0 && `(${selectedUnassignedOrders.length})`}
              </Button>
              {selectedUnassignedOrders.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedUnassignedOrders([])}
                  className="text-slate-500"
                >
                  Clear Selection
                </Button>
              )}
            </div>
            
            {/* Orders Table */}
            <div className="flex-1 overflow-auto border rounded-lg">
              {unassignedLoading ? (
                <div className="flex items-center justify-center h-40">
                  <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : unassignedOrders.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-40 text-slate-500">
                  <Package className="w-10 h-10 mb-2 opacity-50" />
                  <p>No unassigned orders found</p>
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-slate-100 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left w-10">
                        <input
                          type="checkbox"
                          checked={selectedUnassignedOrders.length === unassignedOrders.length && unassignedOrders.length > 0}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedUnassignedOrders([...unassignedOrders]);
                            } else {
                              setSelectedUnassignedOrders([]);
                            }
                          }}
                          className="rounded border-slate-300"
                        />
                      </th>
                      <th className="px-3 py-2 text-left">Tracking</th>
                      <th className="px-3 py-2 text-left">Product</th>
                      <th className="px-3 py-2 text-right">Medicine Cost</th>
                      <th className="px-3 py-2 text-right">Shipping</th>
                      <th className="px-3 py-2 text-right">Total Cost</th>
                      <th className="px-3 py-2 text-left">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unassignedOrders.map((order, idx) => {
                      const isSelected = selectedUnassignedOrders.some(o => o.tracking === order.tracking);
                      return (
                        <tr 
                          key={order.tracking || idx}
                          className={`border-b hover:bg-slate-50 cursor-pointer ${isSelected ? 'bg-blue-50' : ''}`}
                          onClick={() => {
                            if (isSelected) {
                              setSelectedUnassignedOrders(prev => prev.filter(o => o.tracking !== order.tracking));
                            } else {
                              setSelectedUnassignedOrders(prev => [...prev, order]);
                            }
                          }}
                        >
                          <td className="px-3 py-2">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => {}}
                              className="rounded border-slate-300"
                            />
                          </td>
                          <td className="px-3 py-2 font-mono text-xs">{order.tracking || '-'}</td>
                          <td className="px-3 py-2 max-w-[200px] truncate">{order.product_name || '-'}</td>
                          <td className="px-3 py-2 text-right text-green-600">{formatCurrency(order.medicine_cost)}</td>
                          <td className="px-3 py-2 text-right text-orange-600">{formatCurrency(order.shipping_cost)}</td>
                          <td className="px-3 py-2 text-right font-semibold">{formatCurrency(order.total_cost)}</td>
                          <td className="px-3 py-2 text-slate-600">{order.order_date || order.date || '-'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            
            {/* Pagination */}
            {unassignedTotalPages > 1 && (
              <div className="flex items-center justify-between pt-2 border-t">
                <span className="text-sm text-slate-600">
                  Page {unassignedPage} of {unassignedTotalPages}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fetchUnassignedOrders(unassignedPage - 1, unassignedSearch)}
                    disabled={unassignedPage <= 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fetchUnassignedOrders(unassignedPage + 1, unassignedSearch)}
                    disabled={unassignedPage >= unassignedTotalPages}
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Account Aliases Management Dialog */}
      <Dialog open={showAliasDialog} onOpenChange={setShowAliasDialog}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between gap-2">
              <span className="flex items-center gap-2">
                <List className="w-5 h-5 text-amber-500" />
                Account Alias Management
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  if (!window.confirm("Scan every shipment client_code and ensure their ledger doc has a role. Missing docs get a stub with role=partner. Continue?")) return;
                  setAliasHealthLoading(true);
                  try {
                    const r = await fetch(`${BACKEND_URL}/api/admin/fix-ledger-roles`, {
                      method: "POST",
                      headers: { Authorization: `Bearer ${token}` },
                    });
                    const d = await r.json();
                    if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
                    toast.success(`Roles fixed — created ${d.created}, updated ${d.updated}, untouched ${d.untouched}`);
                  } catch (e) {
                    toast.error(`Fix roles failed: ${e.message}`);
                  } finally {
                    setAliasHealthLoading(false);
                  }
                }}
                disabled={aliasHealthLoading}
                className="h-7 text-xs gap-1 border-amber-300 text-amber-700 hover:bg-amber-50"
                data-testid="fix-ledger-roles-btn"
                title="Auto-assign role=partner to ledger accounts missing/blank roles"
              >
                🔧 Fix Roles
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchAliasHealth}
                disabled={aliasHealthLoading}
                className="h-7 text-xs gap-1 border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                data-testid="alias-health-check-btn"
                title="Run a cascade-drift scan across every collection"
              >
                {aliasHealthLoading ? "Checking…" : "🩺 Health Check"}
              </Button>
            </DialogTitle>
          </DialogHeader>

          {/* Health Check banner */}
          {aliasHealth && (
            <div
              className={`rounded-lg p-3 text-xs border ${
                aliasHealth.total_drifted_rows === 0
                  ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                  : "bg-amber-50 border-amber-200 text-amber-900"
              }`}
              data-testid="alias-health-banner"
            >
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div>
                  <span className="font-semibold">
                    {aliasHealth.total_drifted_rows === 0 ? "✅ All clean — " : "⚠️ Drift detected — "}
                  </span>
                  {aliasHealth.alias_count_clean} of {aliasHealth.total_aliases} aliases fully cascaded
                  {aliasHealth.total_drifted_rows > 0 && (
                    <span>; {aliasHealth.total_drifted_rows.toLocaleString()} row(s) still using alias spelling</span>
                  )}
                </div>
                {aliasHealth.total_drifted_rows > 0 && (
                  <Button
                    size="sm"
                    onClick={runAliasCascadeAll}
                    disabled={aliasHealthLoading}
                    className="h-7 text-xs bg-amber-600 hover:bg-amber-700 text-white"
                    data-testid="alias-run-cascade-btn"
                  >
                    Run cascade now
                  </Button>
                )}
              </div>
              {aliasHealth.items?.filter((it) => it.drift_total > 0).length > 0 && (
                <details className="mt-2 text-[11px]" data-testid="alias-health-details">
                  <summary className="cursor-pointer font-semibold">Show drifted aliases</summary>
                  <table className="w-full mt-1 border-collapse">
                    <thead>
                      <tr className="bg-amber-100/60">
                        <th className="px-2 py-1 text-left">Alias</th>
                        <th className="px-2 py-1 text-left">Canonical</th>
                        <th className="px-2 py-1 text-right">Rows</th>
                        <th className="px-2 py-1 text-left">By collection</th>
                      </tr>
                    </thead>
                    <tbody>
                      {aliasHealth.items.filter((it) => it.drift_total > 0).map((it) => (
                        <tr key={it.alias} className="border-t border-amber-200">
                          <td className="px-2 py-1 font-mono">{it.alias}</td>
                          <td className="px-2 py-1 font-mono text-emerald-700">→ {it.canonical_code}</td>
                          <td className="px-2 py-1 text-right font-semibold">{it.drift_total}</td>
                          <td className="px-2 py-1 text-[10px]">
                            {Object.entries(it.by_collection).map(([c, n]) => `${c}=${n}`).join(", ")}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </details>
              )}
            </div>
          )}
          
          {/* Tabs */}
          <div className="flex gap-2 border-b pb-2">
            <Button
              variant={aliasTab === "aliases" ? "default" : "outline"}
              size="sm"
              onClick={() => setAliasTab("aliases")}
            >
              Aliases ({aliases.length})
            </Button>
            <Button
              variant={aliasTab === "suggestions" ? "default" : "outline"}
              size="sm"
              onClick={() => setAliasTab("suggestions")}
              className={aliasSuggestions.length > 0 ? "border-green-300" : ""}
            >
              Suggestions ({aliasSuggestions.length})
              {aliasSuggestions.length > 0 && <span className="ml-1 w-2 h-2 bg-green-500 rounded-full" />}
            </Button>
            <Button
              variant={aliasTab === "duplicates" ? "default" : "outline"}
              size="sm"
              onClick={() => setAliasTab("duplicates")}
              className={duplicates.length > 0 ? "border-red-300" : ""}
            >
              Duplicates ({duplicates.length})
              {duplicates.length > 0 && <span className="ml-1 w-2 h-2 bg-red-500 rounded-full" />}
            </Button>
            <Button
              variant={aliasTab === "history" ? "default" : "outline"}
              size="sm"
              onClick={async () => {
                setAliasTab("history");
                setAliasHistoryLoading(true);
                try {
                  const res = await fetch(`${BACKEND_URL}/api/client-aliases/history`, {
                    headers: { Authorization: `Bearer ${token}` },
                  });
                  const data = await res.json();
                  if (res.ok) setAliasHistory(data.history || []);
                  else toast.error(data.detail || "Failed to load history");
                } catch (e) {
                  toast.error("Network error: " + e.message);
                } finally {
                  setAliasHistoryLoading(false);
                }
              }}
              data-testid="alias-history-tab"
            >
              History
            </Button>
          </div>
          
          <div className="flex-1 overflow-auto">
            {/* Aliases Tab */}
            {aliasTab === "aliases" && (
              <div className="space-y-4">
                {/* Add New Alias Form */}
                <div className="bg-slate-50 p-4 rounded-lg border">
                  <h3 className="text-sm font-semibold text-slate-700 mb-3">Create New Alias</h3>
                  <div className="flex gap-3 items-end">
                    <div className="flex-1">
                      <Label className="text-xs text-slate-500">Alias (short name)</Label>
                      <Input
                        placeholder="e.g., EXP, HB, MED"
                        value={newAlias.alias}
                        onChange={(e) => setNewAlias({ ...newAlias, alias: e.target.value.toUpperCase() })}
                        className="mt-1"
                      />
                    </div>
                    <div className="text-slate-400 text-lg pb-2">→</div>
                    <div className="flex-1">
                      <Label className="text-xs text-slate-500">Maps to (canonical client code)</Label>
                      <Select 
                        value={newAlias.canonical_code} 
                        onValueChange={(v) => setNewAlias({ ...newAlias, canonical_code: v })}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Select client..." />
                        </SelectTrigger>
                        <SelectContent>
                          {ledgers.map(l => (
                            <SelectItem key={l.client_code} value={l.client_code}>
                              {l.client_code} {l.name ? `(${l.name})` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button onClick={createAlias} className="gap-1">
                      <Plus className="w-4 h-4" />
                      Add
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    When uploading sales sheets, any client code matching the alias will automatically be mapped to the canonical code.
                  </p>
                </div>
                
                {/* Existing Aliases List */}
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-2">
                    Existing Aliases
                    <Button variant="ghost" size="sm" onClick={fetchAliases} className="ml-2">
                      <RefreshCw className={`w-3 h-3 ${aliasesLoading ? 'animate-spin' : ''}`} />
                    </Button>
                  </h3>
                  {aliasesLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
                    </div>
                  ) : aliases.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      No aliases configured yet. Create one above.
                    </div>
                  ) : (
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-100">
                          <tr>
                            <th className="text-left px-3 py-2 font-medium text-slate-600">Alias</th>
                            <th className="text-left px-3 py-2 font-medium text-slate-600">Maps To</th>
                            <th className="text-left px-3 py-2 font-medium text-slate-600">Created</th>
                            <th className="text-left px-3 py-2 font-medium text-slate-600">Source</th>
                            <th className="px-3 py-2"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {aliases.map((a, idx) => (
                            <tr key={a.alias || idx} className="border-t hover:bg-slate-50">
                              <td className="px-3 py-2 font-mono font-semibold text-amber-700">{a.alias}</td>
                              <td className="px-3 py-2 font-mono text-indigo-600">{a.canonical_code}</td>
                              <td className="px-3 py-2 text-slate-500 text-xs">
                                {a.created_at ? formatDMY(a.created_at) : '-'}
                              </td>
                              <td className="px-3 py-2">
                                {a.auto_created ? (
                                  <span className="text-xs px-2 py-0.5 bg-slate-200 text-slate-600 rounded">Auto</span>
                                ) : (
                                  <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">Manual</span>
                                )}
                              </td>
                              <td className="px-3 py-2 text-right">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => deleteAlias(a.alias)}
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                
                {/* Fix Historical Data Section */}
                {aliases.length > 0 && (
                  <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg mt-4">
                    <h3 className="text-sm font-semibold text-amber-800 mb-2">Fix Historical Data</h3>
                    <p className="text-xs text-amber-700 mb-3">
                      Update existing records in the database to use canonical client codes instead of aliases. 
                      This permanently fixes historical data so aliased records appear under the correct account.
                    </p>
                    <div className="flex gap-2 flex-wrap">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => fixHistoricalAliases(true)}
                        disabled={aliasFixLoading}
                        className="border-amber-300 text-amber-700 hover:bg-amber-100"
                      >
                        {aliasFixLoading ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : null}
                        Preview Changes
                      </Button>
                      <Button 
                        size="sm" 
                        onClick={() => {
                          if (window.confirm("This will permanently update all records. Are you sure?")) {
                            fixHistoricalAliases(false);
                          }
                        }}
                        disabled={aliasFixLoading}
                        className="bg-amber-600 hover:bg-amber-700 text-white"
                      >
                        Apply Fix
                      </Button>
                      <Button
                        size="sm"
                        onClick={async () => {
                          if (!window.confirm("Run the full cascade for ALL aliases?\n\nThis covers Payment Follow-Ups, Partner Ledger Uploads, CRM, and every other collection — and removes redundant alias ledger rows. Safe to re-run.")) return;
                          try {
                            const res = await fetch(`${BACKEND_URL}/api/client-aliases/run-cascade-all`, {
                              method: "POST",
                              headers: { Authorization: `Bearer ${token}` },
                            });
                            const data = await res.json();
                            if (!res.ok) { toast.error(data.detail || "Cascade failed"); return; }
                            const t = data.totals || {};
                            const parts = [];
                            if (t.payment_followups) parts.push(`${t.payment_followups} follow-ups`);
                            if (t.shipment_orders) parts.push(`${t.shipment_orders} orders`);
                            if (t.sales) parts.push(`${t.sales} sales`);
                            if (t.ledger_entries) parts.push(`${t.ledger_entries} ledger entries`);
                            if (t.partner_ledger_uploads) parts.push(`${t.partner_ledger_uploads} partner uploads`);
                            if (t.alias_ledgers_dropped) parts.push(`${t.alias_ledgers_dropped} alias ledger row(s) removed`);
                            toast.success(parts.length ? `Cascade complete: ${parts.join(", ")}` : "Cascade complete — nothing to merge");
                            fetchAliases();
                            fetchLedgers();
                          } catch (e) {
                            toast.error("Network error: " + e.message);
                          }
                        }}
                        data-testid="run-cascade-all-btn"
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      >
                        Run Full Cascade Now
                      </Button>
                    </div>
                    <p className="text-[11px] text-amber-700 mt-2 italic">
                      "Run Full Cascade Now" covers <b>all</b> collections (follow-ups, partner uploads, CRM, etc.) — use this when names still appear duplicated in any panel.
                    </p>
                    
                    {/* Fix Results */}
                    {aliasFixResult && (
                      <div className="mt-3 p-3 bg-white rounded border text-xs">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`px-2 py-0.5 rounded ${aliasFixResult.dry_run ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                            {aliasFixResult.dry_run ? 'Preview' : 'Applied'}
                          </span>
                          <span className="font-semibold">
                            {aliasFixResult.total_records_affected} records {aliasFixResult.dry_run ? 'would be' : 'were'} updated
                          </span>
                        </div>
                        {Object.entries(aliasFixResult.changes || {}).map(([collection, data]) => (
                          data.total_records > 0 && (
                            <div key={collection} className="mt-2">
                              <span className="font-medium text-slate-700">{collection}:</span>
                              <span className="ml-2 text-slate-600">{data.total_records} records</span>
                              <div className="ml-4 text-slate-500">
                                {data.details?.map((d, i) => (
                                  <div key={i}>{d.alias} → {d.canonical}: {d.count}</div>
                                ))}
                              </div>
                            </div>
                          )
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {/* Suggestions Tab */}
            {aliasTab === "suggestions" && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-slate-600">
                    Auto-detected {aliasSuggestions.length} potential alias(es) — covers month prefixes ("DEC AHEIRON" → "AHEIRON"), trailing numbers, and <b>typos</b> (edit-distance ≤ 2 against existing ledger codes).
                  </p>
                  <Button variant="outline" size="sm" onClick={fetchAliasSuggestions}>
                    <RefreshCw className={`w-3 h-3 mr-1 ${suggestionsLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>

                {suggestionsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
                  </div>
                ) : aliasSuggestions.length === 0 ? (
                  <div className="text-center py-8 text-emerald-600 bg-emerald-50 rounded-lg">
                    <span className="text-2xl">✓</span>
                    <p className="mt-2">No alias suggestions at this time!</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {aliasSuggestions.map((s, idx) => (
                      <div key={s.alias || idx} className={`border rounded-lg p-3 flex items-center justify-between ${s.pattern === 'typo' ? 'bg-rose-50/50 border-rose-200' : 'bg-green-50/50 border-green-200'}`}>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono text-sm font-semibold text-amber-700">{s.alias}</span>
                            <span className="text-slate-400">→</span>
                            <span className="font-mono text-sm text-indigo-600">{s.canonical_code}</span>
                            {s.pattern === 'typo' && (
                              <span className="px-1.5 py-0.5 bg-rose-100 text-rose-700 rounded text-[10px] font-semibold">
                                TYPO · {s.edit_distance} edit{s.edit_distance > 1 ? 's' : ''}
                              </span>
                            )}
                            {s.pattern === 'month_prefix' && (
                              <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px] font-semibold">MONTH</span>
                            )}
                            {s.pattern === 'trailing_number' && (
                              <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-[10px] font-semibold">VARIANT</span>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 mt-1">{s.reason}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => createAliasFromSuggestion(s)}
                            className="bg-green-600 hover:bg-green-700 text-white"
                          >
                            <Check className="w-3 h-3 mr-1" /> Accept
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setAliasSuggestions(prev => prev.filter(x => x.alias !== s.alias))}
                            className="text-slate-400 hover:text-slate-600"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}

                    {aliasSuggestions.length > 1 && (
                      <Button
                        size="sm"
                        onClick={async () => {
                          for (const s of aliasSuggestions) {
                            await createAliasFromSuggestion(s);
                          }
                        }}
                        className="w-full bg-green-600 hover:bg-green-700 text-white mt-3"
                      >
                        Accept All Suggestions ({aliasSuggestions.length})
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {/* Duplicates Tab */}
            {aliasTab === "duplicates" && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-slate-600">
                    Found {duplicates.length} potential duplicate client group(s). Merge them to consolidate data.
                  </p>
                  <Button variant="outline" size="sm" onClick={fetchDuplicates}>
                    <RefreshCw className={`w-3 h-3 mr-1 ${duplicatesLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>
                
                {duplicatesLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
                  </div>
                ) : duplicates.length === 0 ? (
                  <div className="text-center py-8 text-emerald-600 bg-emerald-50 rounded-lg">
                    <span className="text-2xl">✓</span>
                    <p className="mt-2">No duplicate clients detected!</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {duplicates.map((dup, idx) => (
                      <div key={dup.normalized || idx} className="border rounded-lg p-4 bg-amber-50/50 border-amber-200">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <span className="text-xs text-slate-500">Normalized: </span>
                            <span className="font-mono text-sm text-slate-700">{dup.normalized}</span>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            className="gap-1 border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                            onClick={() => {
                              const codes = dup.codes.map(c => c.code);
                              setMergeData({
                                source_codes: codes.filter(c => c !== dup.suggested_canonical),
                                target_code: dup.suggested_canonical
                              });
                              setShowMergeDialog(true);
                            }}
                          >
                            <Zap className="w-3 h-3" />
                            Merge
                          </Button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {dup.codes.map(c => (
                            <div 
                              key={c.code}
                              className={`px-3 py-1.5 rounded border text-sm ${
                                c.code === dup.suggested_canonical 
                                  ? 'bg-indigo-100 border-indigo-300 text-indigo-700 font-semibold' 
                                  : 'bg-white border-slate-200'
                              }`}
                            >
                              <span className="font-mono">{c.code}</span>
                              <span className="text-xs text-slate-500 ml-2">
                                ({c.sales_count} sales, {c.orders_count} orders)
                                {c.has_ledger && <span className="ml-1 text-emerald-600">✓ Ledger</span>}
                              </span>
                              {c.code === dup.suggested_canonical && (
                                <span className="ml-2 text-xs bg-indigo-200 px-1.5 py-0.5 rounded">Suggested</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* History Tab */}
            {aliasTab === "history" && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-slate-600">
                    Every alias merge cascade ever performed. Undo restores the alias ledger row and removes the mapping.
                    Cascaded data (orders, follow-ups) stays under canonical.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      setAliasHistoryLoading(true);
                      try {
                        const res = await fetch(`${BACKEND_URL}/api/client-aliases/history`, {
                          headers: { Authorization: `Bearer ${token}` },
                        });
                        const data = await res.json();
                        if (res.ok) setAliasHistory(data.history || []);
                      } finally { setAliasHistoryLoading(false); }
                    }}
                  >
                    <RefreshCw className={`w-3 h-3 mr-1 ${aliasHistoryLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>

                {aliasHistoryLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
                  </div>
                ) : aliasHistory.length === 0 ? (
                  <div className="text-center py-8 text-slate-500 bg-slate-50 rounded-lg">
                    No alias merges yet.
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[60vh] overflow-auto">
                    {aliasHistory.map((h) => (
                      <div
                        key={h.id}
                        className="bg-white border border-slate-200 rounded px-3 py-2 flex items-start justify-between gap-3"
                        data-testid={`alias-history-${h.id}`}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <code className="font-mono text-sm text-slate-800">{h.alias}</code>
                            <span className="text-slate-400">→</span>
                            <code className="font-mono text-sm text-slate-800">{h.canonical_code}</code>
                            {h.restored && (
                              <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded text-[10px] font-semibold">UNDONE</span>
                            )}
                          </div>
                          <div className="text-[11px] text-slate-500 mt-0.5">
                            by <b>{h.performed_by}</b> · {h.timestamp ? new Date(h.timestamp).toLocaleString() : ""}
                          </div>
                          {h.cascade_summary && Object.keys(h.cascade_summary).length > 0 && (
                            <div className="text-[11px] text-slate-600 mt-1 flex flex-wrap gap-1">
                              {Object.entries(h.cascade_summary).map(([k, v]) => (
                                <span key={k} className="bg-slate-100 px-1.5 py-0.5 rounded">
                                  {k}: <b>{v}</b>
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        {!h.restored && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 text-xs flex-shrink-0"
                            onClick={() => { setUndoPrompt({ id: h.id, alias: h.alias, canonical_code: h.canonical_code }); setUndoPassword(""); }}
                            data-testid={`alias-undo-${h.id}`}
                          >
                            <RefreshCw className="w-3 h-3 mr-1" />
                            Undo
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAliasDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Merge Confirmation Dialog */}
      <Dialog open={showMergeDialog} onOpenChange={setShowMergeDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <Zap className="w-5 h-5" />
              Merge Clients
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              This will merge all sales, orders, and ledger entries from the source codes into the target code.
              Aliases will be created automatically.
            </p>
            
            <div>
              <Label className="text-xs text-slate-500">Source Codes (will be merged from)</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {mergeData.source_codes.map(code => (
                  <span key={code} className="px-2 py-1 bg-red-100 text-red-700 rounded text-sm font-mono">
                    {code}
                  </span>
                ))}
              </div>
            </div>
            
            <div>
              <Label className="text-xs text-slate-500">Target Code (will merge into)</Label>
              <Select 
                value={mergeData.target_code} 
                onValueChange={(v) => setMergeData({ ...mergeData, target_code: v })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select target..." />
                </SelectTrigger>
                <SelectContent>
                  {[...mergeData.source_codes, mergeData.target_code].filter(Boolean).map(code => (
                    <SelectItem key={code} value={code}>{code}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-800">
              <strong>Warning:</strong> This action cannot be undone. The source codes will be deleted 
              and their data will be moved to the target code.
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMergeDialog(false)}>
              Cancel
            </Button>
            <Button onClick={mergeDuplicates} className="bg-amber-600 hover:bg-amber-700">
              <Zap className="w-4 h-4 mr-1" />
              Merge Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Undo Alias Merge Dialog */}
      <Dialog open={!!undoPrompt} onOpenChange={(o) => !undoLoading && !o && setUndoPrompt(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-700">
              <RefreshCw className="w-5 h-5" />
              Undo Alias Merge
            </DialogTitle>
          </DialogHeader>
          {undoPrompt && (
            <div className="space-y-3">
              <div className="bg-slate-50 border rounded p-3 text-sm">
                <code className="font-mono">{undoPrompt.alias}</code>
                <span className="text-slate-400 mx-2">→</span>
                <code className="font-mono">{undoPrompt.canonical_code}</code>
              </div>
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                The alias mapping will be removed and the alias ledger row will be re-created. <b>Previously cascaded data (orders, follow-ups) stays under the canonical code</b> — we don't track which records came from where.
              </div>
              <div>
                <Label className="text-xs">Admin Password</Label>
                <Input
                  type="password"
                  value={undoPassword}
                  onChange={(e) => setUndoPassword(e.target.value)}
                  autoFocus
                  placeholder="Super admin password"
                  data-testid="alias-undo-password"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setUndoPrompt(null)} disabled={undoLoading}>
              Cancel
            </Button>
            <Button
              onClick={async () => {
                if (!undoPassword) { toast.error("Password required"); return; }
                setUndoLoading(true);
                try {
                  const res = await fetch(`${BACKEND_URL}/api/client-aliases/undo-merge`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                    body: JSON.stringify({ log_id: undoPrompt.id, admin_password: undoPassword }),
                  });
                  const data = await res.json();
                  if (!res.ok) { toast.error(data.detail || "Undo failed"); return; }
                  toast.success(`Undone: ${data.summary?.alias} ledger restored, mapping removed`);
                  setUndoPrompt(null);
                  setUndoPassword("");
                  // Refresh history + aliases + ledgers
                  const r2 = await fetch(`${BACKEND_URL}/api/client-aliases/history`, { headers: { Authorization: `Bearer ${token}` } });
                  const d2 = await r2.json();
                  if (r2.ok) setAliasHistory(d2.history || []);
                  fetchAliases();
                  fetchLedgers();
                } catch (e) {
                  toast.error("Network error: " + e.message);
                } finally {
                  setUndoLoading(false);
                }
              }}
              disabled={undoLoading}
              data-testid="alias-undo-confirm"
            >
              {undoLoading ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : null}
              Undo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Alias Cleanup Tool */}
      <Dialog open={aliasCleanupOpen} onOpenChange={setAliasCleanupOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="alias-cleanup-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eraser className="w-4 h-4 text-amber-600" /> Alias Cleanup — Junk Client Names
            </DialogTitle>
            <DialogDescription>
              Codes with junk characters (quotes, stars, etc.) whose cleaned name matches a real partner. Merging creates an alias so all their data folds into the real partner everywhere, and removes the junk ledger row.
            </DialogDescription>
          </DialogHeader>
          {aliasScan.loading ? (
            <div className="py-10 text-center"><RefreshCw className="w-5 h-5 animate-spin mx-auto text-amber-500" /><p className="text-xs text-slate-500 mt-2">Scanning all collections...</p></div>
          ) : aliasScan.candidates.length === 0 ? (
            <p className="py-8 text-center text-sm text-emerald-600 font-medium">✓ No junk-charactered client names found — everything is clean</p>
          ) : (
            <div className="space-y-1.5">
              {aliasScan.candidates.map((c) => (
                <label key={c.alias} className="flex items-center gap-3 p-2 rounded-lg border hover:bg-amber-50/50 cursor-pointer" data-testid={`alias-cand-${c.alias}`}>
                  <input type="checkbox" checked={!!aliasSelected[c.alias]}
                    onChange={() => setAliasSelected((p) => ({ ...p, [c.alias]: !p[c.alias] }))} className="rounded" />
                  <span className="font-mono text-sm font-bold text-red-600">{c.alias}</span>
                  <span className="text-slate-400">→</span>
                  <span className="font-mono text-sm font-bold text-emerald-700">{c.canonical}</span>
                  <span className="ml-auto text-[10px] text-slate-500">
                    {c.total_docs || 0} docs{c.has_ledger_row ? " · junk ledger row" : ""}
                  </span>
                </label>
              ))}
              <div className="flex justify-end gap-2 pt-3 border-t">
                <Button variant="outline" size="sm" onClick={() => setAliasCleanupOpen(false)}>Cancel</Button>
                <Button size="sm" onClick={mergeAliases} disabled={aliasMerging}
                  className="bg-amber-600 hover:bg-amber-700 gap-1" data-testid="alias-merge-btn">
                  {aliasMerging ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Eraser className="w-3.5 h-3.5" />}
                  Merge Selected ({aliasScan.candidates.filter((c) => aliasSelected[c.alias]).length})
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Role Review Queue */}
      <Dialog open={roleReviewOpen} onOpenChange={(o) => { setRoleReviewOpen(o); if (!o) fetchLedgers(); }}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="role-review-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCheck className="w-4 h-4 text-sky-600" /> Role Review — Unconfirmed Auto-created Clients
            </DialogTitle>
            <DialogDescription>
              These clients were auto-created during uploads/sync and their role was never confirmed. Until confirmed they appear in Pending Sales &amp; Follow-up. Click a role to confirm — "Uploads sheets" means they submit partner ledger sheets (almost certainly a Partner).
            </DialogDescription>
          </DialogHeader>
          {roleReview.loading ? (
            <div className="py-10 text-center"><RefreshCw className="w-5 h-5 animate-spin mx-auto text-sky-500" /><p className="text-xs text-slate-500 mt-2">Loading queue...</p></div>
          ) : roleReview.items.length === 0 ? (
            <p className="py-8 text-center text-sm text-emerald-600 font-medium">✓ No unconfirmed clients — every role is classified</p>
          ) : (
            <div className="space-y-1.5">
              <div className="flex justify-end">
                <Button size="sm" variant="outline" disabled={!!roleReviewSaving}
                  onClick={() => confirmRoles(roleReview.items.map((i) => ({ client_code: i.client_code, role: i.suggested_role })))}
                  className="gap-1 border-sky-300 text-sky-700 hover:bg-sky-50" data-testid="role-review-apply-all">
                  {roleReviewSaving === "__all__" ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <UserCheck className="w-3.5 h-3.5" />}
                  Apply all suggestions ({roleReview.items.length})
                </Button>
              </div>
              {roleReview.items.map((i) => (
                <div key={i.client_code} className="flex items-center gap-3 p-2 rounded-lg border hover:bg-sky-50/40" data-testid={`role-review-row-${i.client_code}`}>
                  <div className="min-w-0 flex-1">
                    <span className="font-mono text-sm font-bold text-slate-800">{i.client_code}</span>
                    <div className="flex items-center gap-2 text-[10px] text-slate-500 mt-0.5">
                      <span>{i.orders.toLocaleString()} orders</span>
                      {i.uploads_sheets && <span className="px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 font-medium">Uploads sheets</span>}
                      {i.current_role && <span>machine role: {i.current_role}</span>}
                    </div>
                  </div>
                  {["Partner", "Agent", "DP Client"].map((r) => (
                    <Button key={r} size="sm" variant={i.suggested_role === r ? "default" : "outline"}
                      disabled={!!roleReviewSaving}
                      onClick={() => confirmRoles([{ client_code: i.client_code, role: r }])}
                      className={`h-7 px-2.5 text-xs ${i.suggested_role === r ? "bg-sky-600 hover:bg-sky-700" : ""}`}
                      data-testid={`role-btn-${i.client_code}-${r.replace(" ", "-")}`}>
                      {roleReviewSaving === i.client_code && i.suggested_role === r ? <RefreshCw className="w-3 h-3 animate-spin" /> : r}
                    </Button>
                  ))}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LedgerPage;
